import type {
  BallInPlayAction,
  Destination,
  Direction,
  ResultAction,
  RunnerMovementAction,
  ScoringAction
} from '../../common/scoring/actions'
import {
  baseDestination,
  destinationBase,
  RUNNER_REASONS
} from '../../common/scoring/actions'
import {
  ballWentThere,
  BASE_FOR_OUT,
  FOR_THE_OUT,
  FORCE_AT,
  OUT_BASE,
  OUT_BASES,
  type OutBase,
  OVERTHROW,
  RUNNER_GOES_ON,
  TAKES_BASE
} from '../../common/scoring/baseOutWords'
import { type Depth, FOUL_DEPTHS } from '../../common/scoring/depth'
import { pitchTypeSaid } from '../../common/scoring/pitchType'
import {
  type Decision,
  forcedFrom,
  nextDecision,
  runnerDecisionError
} from '../../common/scoring/rules'
import { applyAction } from '../../common/scoring/session'
import {
  fenceOf,
  type GameState,
  type Position,
  POSITIONS
} from '../../common/scoring/state'
import { fieldingLocation } from '../field/fieldingLocation'
import { BASES } from '../field/fieldModel'
import { OUTFIELD_RADIUS } from '../field/stadiumBoundary'
import { baseSaid, depthOf, depthSaid } from './dictatedDepth'
import {
  BASE_BEARING,
  type BaseName,
  bearingOf,
  depthPoint,
  depthUnits,
  directionPoint,
  FOUL_ANGLE,
  foulPoint,
  foulSide,
  positionOf,
  SPRAY,
  unitsOfFeet
} from './dictatedGeometry'
import { fieldPlayersFor } from './fieldAdapter'
import { scoringDefensiveRoute, scoringFieldPlayers } from './scoringMotion'
import type { DictationQuestion } from './scoringTypes'

/**
 * A dictated sentence never leaves the scorer an on-screen step.
 *
 * A tapped play can stop to ask the scorer something only the screen can be
 * told — who put the batter out, where the batter went, who made the error,
 * whether the runner sent home was safe. A sentence that leaves the play
 * waiting like that is finished here: by rule where the answer is clear, and
 * by a question for the dictation control where it is not. A throw the scorer
 * did not name is credited to the fielder the ball went to and the fielder
 * who covers the base it was thrown to.
 */

const WAITS: readonly Decision[] = [
  'batterOut',
  'resolveBatter',
  'errorAttribution',
  'homeDecision'
]
const BASE_NAMES: Record<number, Destination> = {
  0: 'batter',
  1: '1B',
  2: '2B',
  3: '3B',
  4: 'home'
}
const SPOKEN: Record<Destination, string> = {
  batter: 'the plate',
  '1B': 'first',
  '2B': 'second',
  '3B': 'third',
  home: 'home'
}

type Question = Omit<DictationQuestion, 'transcript'>
type Step = ScoringAction[] | { ask: Question }

function nameOf(state: GameState, id: number): string {
  return state.gameData.players[id]?.fullName ?? `Player ${id}`
}

function batterOf(state: GameState): number {
  return state.liveData.plays.currentPlay!.matchup.batter.id
}

function out(
  runner: number,
  to: Destination,
  reason?: string,
  fielders?: number[]
): ScoringAction {
  const known = (RUNNER_REASONS as readonly string[]).includes(reason ?? '')
  return {
    type: 'runnerMovement',
    runner,
    to,
    out: true,
    ...(known ? { reason: reason as (typeof RUNNER_REASONS)[number] } : {}),
    ...(fielders == null ? {} : { fielders })
  }
}

function safe(runner: number, to: Destination): ScoringAction {
  return { type: 'runnerMovement', runner, to }
}

/**
 * The batter safe at first, and every runner he forces who was not put out
 * moved up a base ahead of him, lead runner first.
 */
function batterSafe(
  state: GameState,
  outs: ReadonlySet<number>
): ScoringAction[] {
  const bases = state.pending!.originalBases
  const moves: ScoringAction[] = []
  for (let index = bases.length - 1; index >= 0; index--) {
    const id = bases[index]
    const forced = bases.slice(0, index).every(behind => behind != null)
    const alreadySafe = state.pending!.movements.some(
      move =>
        move.playerId === id && move.decision === 'safe' && move.to >= index + 2
    )
    if (id != null && forced && !outs.has(id) && !alreadySafe)
      moves.push(safe(id, BASE_NAMES[index + 2]))
  }
  return [...moves, safe(batterOf(state), '1B')]
}

/** What the play is waiting on, answered: actions, a question, or nothing. */
function decide(
  state: GameState,
  decision: Decision,
  sentence: readonly ScoringAction[],
  transcript = ''
): Step | null {
  const pending = state.pending
  if (!WAITS.includes(decision) || pending == null) return null
  const batter = batterOf(state)
  switch (decision) {
    case 'batterOut': {
      if (pending.outcome === 'Batter Out — Other')
        return [out(batter, 'batter', 'Batter Out — Other')]
      // Thrown out with no throw said: the fielder the ball went to threw
      // to first ("ground out to short" is 6-3). Otherwise nobody said who
      // put him out, so nobody is credited.
      const thrown = sentence.some(
        action =>
          action.type === 'result' &&
          action.eventType === 'field_out' &&
          action.outBy === 'throw'
      )
      // With a runner put out too, the throw to first came from the fielder
      // who made that out, if the sentence says who did.
      // Only an out on this ball in play, not a caught stealing before it.
      const since = sentence.findLastIndex(
        action => action.type === 'ballInPlay'
      )
      // A force's fielder threw on to first, as in a double play, and so did
      // one who tagged a runner himself. A runner thrown out by a throw of
      // his own (two fielders or more) was put out after the batter.
      const runnerOut = sentence
        .slice(Math.max(0, since))
        .reverse()
        .find(
          action =>
            action.type === 'runnerMovement' &&
            action.out === true &&
            action.runner !== batter &&
            !(
              action.reason === 'Throw Out' &&
              (action.fielders?.length ?? 0) > 1
            )
        )
      const named = [...sentence]
        .reverse()
        .find(action => action.type === 'ballInPlay' && action.fielder != null)
      const fielder =
        runnerOut?.type === 'runnerMovement'
          ? runnerOut.fielders?.at(-1)
          : named?.type === 'ballInPlay'
            ? named.fielder
            : undefined
      const first =
        (fielder == null
          ? undefined
          : namedAt(state, transcript, '1B', fielder)) ??
        state.liveData.linescore.defense['1B']?.id
      if (!thrown || fielder == null || first == null)
        return [out(batter, '1B', pending.outcome)]
      return [
        out(
          batter,
          '1B',
          pending.outcome,
          fielder === first ? [first] : [fielder, first]
        )
      ]
    }
    case 'resolveBatter': {
      if (pending.outcome === "Fielder's Choice") {
        const outs = new Set(
          pending.movements
            .filter(move => move.playerId !== batter && move.decision === 'out')
            .map(move => move.playerId)
        )
        // A fielder's choice always has a runner on: the library refuses one
        // without.
        const runners = pending.originalBases.flatMap((id, index) =>
          id == null ||
          sentence.some(
            action =>
              action.type === 'runnerMovement' &&
              action.runner === id &&
              action.out !== true
          )
            ? []
            : [{ id, next: BASE_NAMES[index + 2] }]
        )
        if (outs.size > 0 || runners.length === 0)
          return batterSafe(state, outs)
        if (runners.length === 1)
          return [
            out(runners[0].id, runners[0].next),
            ...batterSafe(state, new Set([runners[0].id]))
          ]
        return {
          ask: {
            question: "Which runners were out on the fielder's choice?",
            pick: {
              choices: runners.map(runner => ({
                label: `${nameOf(state, runner.id)} out at ${SPOKEN[runner.next]}`,
                actions: [out(runner.id, runner.next)]
              })),
              before: [],
              none: batterSafe(state, new Set())
            }
          }
        }
      }
      if (pending.outcome === 'Dropped Third Strike')
        return {
          ask: {
            question: `Did ${nameOf(state, batter)} reach first on the dropped third strike?`,
            options: [
              { label: 'Safe at first', actions: [safe(batter, '1B')] },
              { label: 'Out at first', actions: [out(batter, '1B')] }
            ]
          }
        }
      // Anyone else still waiting is safe where the play left him.
      return pending.movements
        .filter(move => move.decision === 'pending' && move.to < 4)
        .map(move => safe(move.playerId, BASE_NAMES[Math.max(1, move.to)]))
    }
    case 'errorAttribution': {
      const named = [...sentence]
        .reverse()
        .find(action => action.type === 'ballInPlay' && action.fielder != null)
      const fielder = named?.type === 'ballInPlay' ? named.fielder : undefined
      if (fielder != null)
        return [{ type: 'fieldingPlay', fielders: [fielder], error: fielder }]
      const defense = state.liveData.linescore.defense
      return {
        ask: {
          question: 'Who made the error?',
          options: POSITIONS.flatMap(position => {
            const ref = defense[position]
            return ref == null
              ? []
              : [
                  {
                    label: `${position} ${nameOf(state, ref.id)}`,
                    actions: [
                      {
                        type: 'fieldingPlay' as const,
                        fielders: [ref.id],
                        error: ref.id
                      }
                    ]
                  }
                ]
          })
        }
      }
    }
    default:
      // A runner the sentence did not put out at home scored.
      return [safe(state.homeQueue[0], 'home')]
  }
}

/**
 * The sentence with whatever it leaves the play waiting on, finished — or the
 * question that finishes it, each answer carrying the whole list of actions it
 * comes to. The sentence must already apply.
 */
/** The fielder who takes a throw at a base, where only one does. */
const COVERS: Partial<Record<Destination, 'C' | '3B' | '1B'>> = {
  home: 'C',
  '3B': '3B',
  '1B': '1B'
}

/**
 * Who takes a throw at second. The second baseman covers when the ball went
 * to the shortstop or the third baseman. The shortstop covers when it went
 * to the first baseman, and when it went to anyone else.
 */
function secondCover(
  fielder: number | undefined,
  at: (position: (typeof POSITIONS)[number]) => number | undefined
): '2B' | 'SS' {
  return fielder != null && (fielder === at('SS') || fielder === at('3B'))
    ? '2B'
    : 'SS'
}

/** The fielder who takes a throw at a base, second included. */
function coverAt(
  to: Destination,
  fielder: number | undefined,
  at: (position: (typeof POSITIONS)[number]) => number | undefined
): 'C' | '1B' | '2B' | '3B' | 'SS' | undefined {
  if (to === '2B') return secondCover(fielder, at)
  return COVERS[to]
}
/** Results a ball in play comes to. */
const BATTED: ReadonlySet<string> = new Set([
  'single',
  'double',
  'triple',
  'home_run',
  'field_out',
  'field_error',
  'fielders_choice'
])
const HITS: ReadonlySet<string> = new Set([
  'single',
  'double',
  'triple',
  'home_run'
])
/** Who catches a ball sent a direction: the gaps are the center fielder's. */
const OUTFIELDER: Record<Direction, 'LF' | 'CF' | 'RF'> = {
  left: 'LF',
  left_center: 'CF',
  center: 'CF',
  right_center: 'CF',
  right: 'RF'
}
/** Who takes a ball a direction that never reached the outfield. */
const INFIELDER: Record<Direction, Position> = {
  left: '3B',
  left_center: 'SS',
  center: 'SS',
  right_center: '2B',
  right: '1B'
}
/** "Into the right field seats", "down the left side". */
const FIELD_SIDE = /\b(left|right)(?:[\s-]field\b| side\b)/i
/** "An inside-the-park home run", "an inside-the-parker", "in park". */
const INSIDE_THE_PARK =
  /\b(?:inside|in)[\s-]the[\s-]park(?:er)?\b|\bin[\s-]park\b/i

function isFoulDepth(depth: Depth): depth is (typeof FOUL_DEPTHS)[number] {
  return (FOUL_DEPTHS as readonly Depth[]).includes(depth)
}

/** The positions on the infield, the pitcher and catcher among them. */
const INFIELD: ReadonlySet<Position> = new Set([
  'P',
  'C',
  '1B',
  '2B',
  '3B',
  'SS'
])
/** How deep an infielder goes for a ball in the air: into shallow. */
const INFIELD_REACH: ReadonlySet<Depth> = new Set([
  'shy_of_base',
  'dribbler',
  'infield',
  'past_base',
  'bloop',
  'shallow'
])
/** Depths a ball never gets out of the infield at. */
const NEAR: ReadonlySet<Depth> = new Set([
  'shy_of_base',
  'dribbler',
  'infield',
  'past_base'
])
/** Depths a foul is the corner outfielder's at, not the corner infielder's. */
const OUTFIELD_FOUL: ReadonlySet<Depth> = new Set([
  'shallow',
  'medium',
  'deep',
  'warning_track',
  'wall'
])

/**
 * The side of the field a foul went to: the side its direction or its
 * base says, then the side the words say of the foul, then `foulSide`.
 */
function foulSign(
  game: GameState,
  direction: Direction | undefined,
  base: BaseName | undefined,
  said: '1B' | '3B' | undefined,
  fielder: number | undefined
): 1 | -1 {
  if (direction === 'left' || direction === 'left_center') return -1
  if (direction === 'right' || direction === 'right_center') return 1
  if (base === 'first') return 1
  if (base === 'third') return -1
  if (said != null) return said === '1B' ? 1 : -1
  return foulSide(game, fielder)
}

/**
 * The bearing of a fair ball placed by its depth: its direction, the base
 * its depth phrase names, the fielder who took it — the catcher and the
 * pitcher are in front of the plate, toward the mound — else straight away.
 */
function fairBearing(
  game: GameState,
  ball: BallInPlayAction,
  base: BaseName | undefined
): number {
  if (ball.direction != null) return SPRAY[ball.direction]
  if (base != null) return BASE_BEARING[base]
  const position =
    ball.fielder == null ? undefined : positionOf(game, ball.fielder)
  if (position == null || position === 'C' || position === 'P') return 0
  const standing = fieldPlayersFor(game).find(
    player => player.team === 'defense' && player.playerId === ball.fielder
  )?.position
  return standing == null ? 0 : bearingOf(standing)
}

/**
 * Each fielder as the words name him, and the base an infielder is named by
 * when a ball is hit to him ("pops out to second").
 */
const FIELDER_NOUNS: ReadonlyArray<
  [string, (typeof POSITIONS)[number], string?]
> = [
  ['catcher', 'C'],
  ['pitcher', 'P'],
  ['first baseman', '1B', 'first'],
  ['second baseman', '2B', 'second'],
  ['shortstop|short(?! (?:left|right|center))', 'SS'],
  ['third baseman', '3B', 'third'],
  ['left fielder', 'LF'],
  ['center fielder', 'CF'],
  ['right fielder', 'RF']
]
const CATCHES =
  'makes (?:the|an?)(?: \\w+){0,2} catch|catch(?:es)? (?:it|the ball)|caught it|hauls it in|reels it in|grabs it|snags it|gloves it|squeezes it'
const HIT_TO =
  '(?:pops?|popped|flies|flied|lines|lined|fouls?|fouled|line drive|liner|pop ?up|pop fly|fly ball)'
/**
 * The fielders the words say outright made a catch — "the shortstop makes
 * the catch", "the second baseman grabs it", "caught by the catcher",
 * "fouled out to the catcher", "pops up to short", "lines out to third",
 * "line drive to short, caught" —
 * as against a fielder named some other way, as a thrower, or a base a
 * runner went to ("tags up to third").
 */
function catchersSaid(game: GameState, transcript: string): number[] {
  const defense = game.liveData.linescore.defense
  return FIELDER_NOUNS.flatMap(([noun, position, base]) => {
    const said = new RegExp(
      [
        `\\b(?:${noun})\\b[^.,;]{0,20}?\\b(?:${CATCHES})\\b`,
        `\\bcaught by (?:the )?(?:${noun})\\b`,
        `\\b(?:out|up|${HIT_TO}) (?:out |up )?to (?:the )?(?:${noun})\\b`,
        ...(base == null
          ? []
          : [`\\b${HIT_TO} (?:out |up )?(?:foul )?to (?:the )?${base}\\b`])
      ].join('|'),
      'i'
    ).test(transcript)
    const id = defense[position]?.id
    return said && id != null ? [id] : []
  })
}
/**
 * A foul's side, said of the foul: a side word within a few words after it —
 * "foul pop to first", "fouls out to third", "foul popout to third", "in
 * foul territory near third", "fouled out, caught near third", "foul ball
 * caught by the third baseman" — or "the third base line"; never a runner's
 * or a throw's "to third": the words between stop at "and", "he", a runner,
 * a runner's verb ("tags up to third"), a throw, "safe", or a comma — one
 * that is followed by "caught", or straight by the side ("foul pop, near
 * third"), is crossed.
 */
const FOUL_SIDE =
  /\bfoul(?:s|ed)?\b(?:(?:[\s-]+|,\s*(?=(?:caught|grabbed|snagged|taken)\b))(?!(?:and|but|while|as|then|he|she|they|runners?|man|guy|kid|girl|boy|tags?|tagged|tagging|goes|went|advances?|advanced|moves?|moved|runs?|ran|takes?|took|heads?|headed|scores?|scored|slides?|slid|sliding|throws?|thrown|safe)\b)[\w']+){0,4}?(?:[\s-]+|,\s*)(?:over (?:by|near)|to|at|near|by|down) (?:the )?(first|third)\b|\b(first|third) base (?:line|side)\b/i

function saidSide(
  game: GameState,
  transcript: string
): '1B' | '3B' | undefined {
  // A runner said by name is a runner.
  const offense = game.liveData.linescore.offense
  const words = [offense.first?.id, offense.second?.id, offense.third?.id]
    .flatMap(id => (id == null ? [] : playerPatterns(game, id)))
    .reduce(
      (text, pattern) => text.replace(new RegExp(pattern, 'gi'), 'runner'),
      transcript
    )
  const match = FOUL_SIDE.exec(words)
  const base = match
    ?.slice(1)
    .find(word => word != null)
    ?.toLowerCase()
  return base === 'first' ? '1B' : base === 'third' ? '3B' : undefined
}

/**
 * What a sentence leaves to be assumed, filled in before it is tried:
 * - a hit with no trajectory is a line drive, and a caught foul a pop-up
 *   that was caught;
 * - a ball in play sent after its result, with only runners' moves
 *   between, goes before it;
 * - a caught ball no action names a fielder for, and whose words do not
 *   say who caught it, was caught by the outfielder for the direction it
 *   went, or for a foul by the corner infielder on its side (its direction,
 *   "to first", or away from the batter) — the game records the out only
 *   with a fielder; otherwise the screen asks —
 *   and a caught foul said without a ball in play has one;
 * - a ball sent a direction lands there, and a caught foul in foul
 *   territory on its fielder's side, or away from the batter;
 * - the batter is not also sent out on a caught ball: the catch is his out;
 * - a runner thrown out with no fielders named was thrown out by the one
 *   who fielded the ball — who caught it, or the outfielder for its
 *   direction — to the fielder covering the base. Second base is the
 *   second baseman's when the shortstop or the third baseman fielded it,
 *   and the shortstop's when the first baseman or anyone else did. A chain
 *   the model sent is kept as sent.
 */
export function sentenceDefaults(
  game: GameState,
  said: readonly ScoringAction[],
  transcript = ''
): SentenceDefaults {
  const spoken = afterStrikeThree(game, said, transcript)
  const defense = game.liveData.linescore.defense
  // A batted-ball result followed by its ball in play, with only runners'
  // moves between: the ball in play goes first. A caught foul with no ball
  // in play of its own since the last result gets one, for its fielder.
  const rest = [...spoken]
  const sentence: ScoringAction[] = []
  let inPlaySince = false
  for (let index = 0; index < rest.length; index++) {
    const action = rest[index]
    if (
      action.type === 'result' &&
      BATTED.has(action.eventType) &&
      !inPlaySince
    ) {
      let ahead = index + 1
      while (
        rest[ahead]?.type === 'runnerMovement' ||
        rest[ahead]?.type === 'fieldingPlay'
      )
        ahead++
      // A ball in play with a result of its own after it is the next
      // batter's, not this one's.
      const ownResult = rest
        .slice(ahead + 1)
        .findIndex(next => next.type === 'result' || next.type === 'ballInPlay')
      // Only a result a ball in play comes to: a walk or a hit by pitch
      // after it takes none, and nor does one sent before its own ball in
      // play, as this one was.
      const followingAt = ahead + 1 + ownResult
      const following = rest[followingAt]
      let afterIt = followingAt + 1
      while (
        rest[afterIt]?.type === 'runnerMovement' ||
        rest[afterIt]?.type === 'fieldingPlay'
      )
        afterIt++
      const itsOwn =
        ownResult >= 0 &&
        following.type === 'result' &&
        BATTED.has(following.eventType) &&
        rest[afterIt]?.type !== 'ballInPlay'
      if (rest[ahead]?.type === 'ballInPlay' && !itsOwn) {
        const [inPlay] = rest.splice(ahead, 1)
        sentence.push(inPlay, action)
        continue
      }
    }
    if (action.type === 'ballInPlay') {
      // The result establishes the batter and forced runners before their
      // calls can settle the play. Preserve the calls' chronology, including
      // a home crossing before a tag third out, and stop at any play boundary.
      let ahead = index + 1
      while (
        rest[ahead]?.type === 'runnerMovement' ||
        rest[ahead]?.type === 'fieldingPlay'
      )
        ahead++
      const result = rest[ahead]
      if (
        ahead > index + 1 &&
        result?.type === 'result' &&
        result.eventType === 'fielders_choice'
      ) {
        rest.splice(ahead, 1)
        rest.splice(index + 1, 0, result)
      }
      inPlaySince = true
    }
    if (action.type === 'result') {
      if (
        !inPlaySince &&
        action.eventType === 'field_out' &&
        action.foulCaught === true
      )
        sentence.push({ type: 'ballInPlay', contact: 'swing' })
      inPlaySince = false
    }
    sentence.push(action)
  }
  // A catcher the words name whom no ball in play carries: the model left
  // him out, and the screen asks rather than assume another.
  const carried = new Set(
    spoken.flatMap(action =>
      action.type === 'ballInPlay' && action.fielder != null
        ? [action.fielder]
        : []
    )
  )
  const catchSaid = catchersSaid(game, transcript).filter(
    id => !carried.has(id)
  )
  const side = saidSide(game, transcript)
  // A foul's side as the words give it: a base, or the field's side.
  const field = FIELD_SIDE.exec(transcript)?.[1]?.toLowerCase()
  const foulSaid =
    side ?? (field === 'right' ? '1B' : field === 'left' ? '3B' : undefined)
  const batter = game.liveData.plays.currentPlay?.matchup.batter.id
  const result = sentence.find(
    (action): action is ResultAction => action.type === 'result'
  )
  const foul = result?.eventType === 'field_out' && result.foulCaught === true
  const caught =
    result?.eventType === 'field_out' && (result.outBy === 'catch' || foul)
  const oneBall =
    sentence.filter(action => action.type === 'ballInPlay').length === 1
  // One foul and no ball in play: what the words say of a foul is its.
  const oneFoul =
    sentence.every(action => action.type !== 'ballInPlay') &&
    sentence.filter(action => action.type === 'pitch' && action.call === 'F')
      .length === 1
  // A kind of pitch the words do not name is the model's guess: a breaking
  // ball is no slider.
  const typed = sentence.map((action): ScoringAction => {
    if (
      (action.type !== 'pitch' && action.type !== 'ballInPlay') ||
      action.pitchType == null ||
      pitchTypeSaid(action.pitchType, transcript)
    )
      return action
    const { pitchType: _guess, ...rest } = action
    return rest
  })
  const filled = typed.flatMap((action, index): ScoringAction[] => {
    if (action.type === 'pitch' && action.call === 'F' && oneFoul) {
      const next = { ...action }
      // The depth the words say, when the model gave none or one of theirs.
      const depth = depthOf(transcript, next.depth)
      if (depth != null && isFoulDepth(depth)) next.depth = depth
      if (next.direction == null && next.depth != null) {
        const base = baseSaid(transcript)
        const direction =
          base === 'first' || foulSaid === '1B'
            ? 'right'
            : base === 'third' || foulSaid === '3B'
              ? 'left'
              : undefined
        if (direction != null) next.direction = direction
      }
      return [next]
    }
    if (action.type === 'result') {
      if (action.foulCaught === true && action.outBy == null)
        return [{ ...action, outBy: 'catch' }]
      return [action]
    }
    if (action.type === 'ballInPlay') {
      const own = sentence
        .slice(index + 1)
        .find((next): next is ResultAction => next.type === 'result')
      const next: BallInPlayAction = { ...action }
      const ownFoul = own?.eventType === 'field_out' && own.foulCaught === true
      const ownCatch =
        own?.eventType === 'field_out' && (own.outBy === 'catch' || ownFoul)
      // How far it went, when the words say so: the deepest they say, when
      // the model gave none or one of theirs.
      if (oneBall) {
        const depth = depthOf(transcript, next.depth)
        if (depth != null) next.depth = depth
      }
      // An infielder's ball is no deeper than he can reach: a ground ball
      // stays on the infield, and a pop or a liner short of medium depth.
      const grounder =
        next.contact === 'bunt' ||
        next.trajectory === 'ground_ball' ||
        next.trajectory === 'chopper'
      const fieldedBy =
        next.fielder == null
          ? groundFielder(transcript)
          : POSITIONS.find(place => defense[place]?.id === next.fielder)
      const reach = grounder ? NEAR : INFIELD_REACH
      if (
        next.depth != null &&
        !reach.has(next.depth) &&
        fieldedBy != null &&
        INFIELD.has(fieldedBy)
      ) {
        // The words' depth, or the model's, when it is within his reach:
        // "deep short" is the infield.
        const within = [
          oneBall ? depthSaid(transcript) : undefined,
          action.depth
        ].find(depth => depth != null && reach.has(depth))
        if (within != null) next.depth = within
        else delete next.depth
      }
      // A ball caught at the wall was caught on the track, never off it.
      if (next.depth === 'wall' && ownCatch) next.depth = 'warning_track'
      // A fielder who is not in the field, and whom the words do not name,
      // is the model's guess: the play finds its own.
      if (
        next.fielder != null &&
        !POSITIONS.some(place => defense[place]?.id === next.fielder) &&
        !namedFielder(game, transcript, next.fielder)
      )
        delete next.fielder
      // A home run inside the park is said so; any other is over the fence.
      if (
        own?.eventType === 'home_run' &&
        next.homeRunKind === 'in-park' &&
        !INSIDE_THE_PARK.test(transcript)
      )
        next.homeRunKind = 'out-of-park'
      const base = oneBall ? baseSaid(transcript) : undefined
      if (next.trajectory == null && next.launchAngle == null) {
        if (ownFoul) next.trajectory = 'popup'
        else if (own != null && HITS.has(own.eventType))
          next.trajectory = 'line_drive'
      }
      if (ownCatch && next.fielder == null) {
        // A foul is caught on its side — by its direction, or the words
        // said of the foul, or away from the batter — by the corner
        // infielder, or out in the outfield by the corner outfielder.
        const sign = foulSign(game, next.direction, base, foulSaid, undefined)
        const outfield = next.depth != null && OUTFIELD_FOUL.has(next.depth)
        const near = next.depth != null && NEAR.has(next.depth)
        const fielder = ownFoul
          ? defense[
              outfield ? (sign === 1 ? 'RF' : 'LF') : sign === 1 ? '1B' : '3B'
            ]?.id
          : next.direction != null
            ? defense[
                near
                  ? next.depth === 'dribbler' && next.direction === 'center'
                    ? 'P'
                    : INFIELDER[next.direction]
                  : OUTFIELDER[next.direction]
              ]?.id
            : undefined
        // Only when the words do not say someone else caught it: one they
        // name but the model left out is asked for.
        const other = catchSaid.some(id => id !== fielder)
        if (fielder != null && !other) next.fielder = fielder
      }
      // "Grounder to third" names the third baseman when the play left him off.
      if (next.fielder == null && !ownCatch && oneBall) {
        const position = groundFielder(transcript)
        const id = position == null ? undefined : defense[position]?.id
        if (id != null) next.fielder = id
      }
      // Left center and right center are the center fielder's, unless the
      // scorer named the corner outfielder the model chose.
      if (
        (next.direction === 'left_center' ||
          next.direction === 'right_center') &&
        next.fielder != null
      ) {
        const center = defense.CF?.id
        const corner =
          next.direction === 'left_center' ? defense.LF?.id : defense.RF?.id
        if (
          center != null &&
          next.fielder === corner &&
          !namedFielder(game, transcript, next.fielder)
        )
          next.fielder = center
      }
      // A spot on the plate is a model's placeholder, not where a ball went.
      if (
        next.location != null &&
        Math.hypot(next.location.x, next.location.z) < 1
      )
        delete next.location
      if (next.location == null) {
        const fence = fenceOf(game)
        const units =
          next.distanceFeet != null && next.distanceFeet > 0
            ? unitsOfFeet(next.distanceFeet, fence)
            : next.depth != null
              ? depthUnits(next.depth, fence)
              : undefined
        // Nearly every home run is called deep: its depth places nothing,
        // and only a distance said past the fence moves it.
        const homer = own?.eventType === 'home_run'
        const placed =
          units != null &&
          (!homer || (next.distanceFeet != null && units > OUTFIELD_RADIUS))
        if (ownFoul)
          next.location =
            units == null
              ? foulPoint(game, next.fielder)
              : depthPoint(
                  foulSign(game, next.direction, base, foulSaid, next.fielder) *
                    FOUL_ANGLE,
                  Math.min(units, OUTFIELD_RADIUS)
                )
        else if (placed)
          next.location = depthPoint(
            fairBearing(game, next, base),
            homer ? units : Math.min(units, OUTFIELD_RADIUS)
          )
        else if (next.direction != null)
          next.location = directionPoint(
            next.direction,
            own,
            next.contact === 'bunt' ||
              next.trajectory === 'ground_ball' ||
              next.trajectory === 'chopper'
          )
      }
      return [next]
    }
    if (
      action.type === 'runnerMovement' &&
      action.out === true &&
      caught &&
      action.runner === batter
    )
      return []
    return [action]
  })
  // The base outs the words say, read once for the passes that use them.
  const baseOutWords = baseOutsSaid(transcript, game)
  const settled = holding(
    game,
    choiceRelayLocations(
      game,
      doublePlay(
        game,
        creditThrowOuts(
          game,
          forceThenOverthrow(
            game,
            baseOuts(
              game,
              errorAfterResult(explicitChoice(game, filled, transcript)),
              transcript,
              baseOutWords
            ),
            transcript,
            baseOutWords
          ),
          transcript
        ),
        transcript
      )
    )
  )
  return thirdOut(game, settled, transcript, baseOutWords)
}

/** What was left to be assumed in a sentence, filled in: its actions. */
export function playDefaults(
  game: GameState,
  said: readonly ScoringAction[],
  transcript = ''
): ScoringAction[] {
  return sentenceDefaults(game, said, transcript).actions
}

// ---- Base outs an infielder makes, and nothing after the third out ----

/** The base before each, whose runner it puts out. */
const FROM_INDEX: Record<OutBase, 0 | 1 | 2> = { '2B': 0, '3B': 1, home: 2 }

/** A throw to a base: "throws to second", "flips it over to third". */
const THROWN_TO_BASE = new RegExp(
  `\\b(?:throws?|threw|flips?|flipped|tosses?|tossed|fires?|fired)\\s+(?:it\\s+|the\\s+ball\\s+)?(?:over\\s+)?(?:to\\s+)?(?:the\\s+)?${OUT_BASE}`,
  'gi'
)

/** "Double play at second": the second out at that base. */
const DOUBLE_PLAY_AT = /\bdouble\s+play\s+at\s+(second|2nd)\b/i
/**
 * Words that put the batter out at first: a throw there, to the first
 * baseman, or the batter or a second out said out.
 */
const BATTER_OUT_SAID =
  /\b(?:at|to)\s+(?:the\s+)?(?:first|1st)(?:\s+base(?:man)?)?\b|\bthrows?\s+(?:him|out\s+the\s+batter)(?:\s+out)?\b|\bthrown\s+out\s+at\s+(?:first|1st)\b|\bbatter(?:'s|\s+is)?\s+out\b|\bdouble\s+play\b|\bturns?\s+two\b|\bturned\s+two\b|(?<!\bwith\s)(?<!\bthere\s(?:are|were)\s)\btwo\s+outs\b/i
/** Where a runner stood, not the batter's out: "the runner at first". */
const RUNNER_AT_FIRST =
  /\b(?:runner|man|guy|kid)\s+(?:who\s+was\s+|that\s+was\s+)?(?:at|on|from)\s+(?:first|1st)(?:\s+base)?\b/gi
/** Where the ball went, not the batter's out: "grounder to first". */
const BALL_TO_FIRST =
  /\b(?:grounder|ground\s+ball|bouncer|chopper|roller|dribbler|liner|line\s+drive|pop(?:[\s-]*up|\s+fly)?|fly(?:\s+ball)?|bunt(?:s|ed)?|ball|hits?(?:\s+(?:it|one))?)\s+(?:right\s+|back\s+|over\s+|softly\s+|hard\s+)?to\s+(?:the\s+)?(?:first|1st)(?:\s+base(?:man)?)?\b/gi
/** Words after a wild throw that say the first baseman saved it for the out. */
const SAVED_AT_FIRST =
  /\b(?:scoops?|scooped|digs?\s+it\s+out|dug\s+it\s+out|saves?\s+it|saved\s+it|stretches\s+for\s+it|out\s+at\s+first)\b/i
/** Words in a throw's clause that say it did not get the batter. */
const THROW_FAILED =
  /\b(?:late|not\s+in\s+time|no\s+throw|never\s+throws?|holds?\s+the\s+ball|eats?\s+it)\b/i

/**
 * Whether the words put the batter out at first: not where the ball or a
 * runner was, and not a throw the words say was late, never made, or beaten.
 */
function batterOutSaid(transcript: string): boolean {
  if (BATTER_SAFE.test(transcript)) return false
  const words = transcript
    .replace(DOUBLE_PLAY_AT, ' ')
    .replace(BALL_TO_FIRST, ' ')
    .replace(RUNNER_AT_FIRST, ' ')
  return [...words.matchAll(new RegExp(BATTER_OUT_SAID.source, 'gi'))].some(
    match => {
      const before = words.slice(0, match.index)
      const start = Math.max(
        before.lastIndexOf(','),
        before.lastIndexOf('.'),
        before.lastIndexOf(';'),
        before.search(/\bbut\b(?!.*\bbut\b)/)
      )
      const end = words.slice(match.index).search(/[,.;]/)
      const clause = words.slice(
        start + 1,
        end < 0 ? undefined : match.index + end
      )
      return !THROW_FAILED.test(clause)
    }
  )
}

interface BaseOut {
  base: OutBase
  /** Where the words of it start. */
  at: number
  /** The fielder the words say made it, by position; else the ball's. */
  by?: (typeof POSITIONS)[number]
  /** The words say he threw there. */
  thrown: boolean
}

/** The clause before `at`: back to the last full stop or semicolon. */
function clauseBefore(transcript: string, at: number): string {
  return transcript.slice(0, at).split(/[.;]/).at(-1) ?? ''
}

/** The fielder a clause names by position, the last one said. */
function fielderIn(clause: string): (typeof POSITIONS)[number] | undefined {
  let found: { at: number; position: (typeof POSITIONS)[number] } | undefined
  for (const [noun, position] of FIELDER_NOUNS)
    for (const match of clause.matchAll(new RegExp(`\\b(?:${noun})\\b`, 'gi')))
      if (found == null || match.index > found.at)
        found = { at: match.index, position }
  return found?.position
}

/**
 * Each base out the words say a fielder made at second, third or home, in
 * the order said: a fielder who tags, touches, steps on, takes or gets the
 * base; the base "for the out"; or a force at it. First base is the batter's
 * out, which the rules settle. Taking or getting a base is an out only when
 * a fielder does it or the words say it was for the out: "Casey takes
 * second" is the runner's.
 */
export function baseOutsSaid(transcript: string, game?: GameState): BaseOut[] {
  // A runner on base, or the batter, said by name is no fielder.
  const offense = game?.liveData.linescore.offense
  const runners =
    game == null
      ? []
      : [
          offense?.first?.id,
          offense?.second?.id,
          offense?.third?.id,
          game.liveData.plays.currentPlay?.matchup.batter.id
        ].filter((id): id is number => id != null)
  // Whose the base is: the nearest subject before it in its sentence — a
  // runner, by name or as "the runner", or a fielder, by position — with
  // "he" or "who" the one named before it, a fielder when none was; or a
  // runner's when the words go on to his run.
  const byRunner = (at: number, end: number): boolean => {
    if (RUNNER_GOES_ON.test(transcript.slice(end))) return true
    const before = clauseBefore(transcript, at)
    const last = (pattern: string, upTo = before.length): number =>
      Math.max(
        -1,
        ...[...before.slice(0, upTo).matchAll(new RegExp(pattern, 'gi'))].map(
          m => m.index
        )
      )
    const runnerAt = (upTo?: number): number =>
      Math.max(
        last('\\b(?:runner|batter)\\b|\\btags?\\s+up\\b', upTo),
        ...runners.flatMap(id =>
          playerPatterns(game!, id).map(pattern => last(pattern, upTo))
        )
      )
    const fielderAt = (upTo?: number): number =>
      Math.max(
        ...FIELDER_NOUNS.map(([noun]) => last(`\\b(?:${noun})\\b`, upTo))
      )
    const pronoun = last('\\b(?:he|who)\\b')
    const pronounRunner = pronoun >= 0 && runnerAt(pronoun) > fielderAt(pronoun)
    const runner = Math.max(runnerAt(), pronounRunner ? pronoun : -1)
    const fielder = Math.max(fielderAt(), pronounRunner ? -1 : pronoun)
    return runner > fielder
  }
  const found: BaseOut[] = []
  const add = (base: OutBase, at: number): void => {
    if (found.some(out => out.base === base)) return
    const clause = clauseBefore(transcript, at)
    const thrown = [...transcript.matchAll(THROWN_TO_BASE)].some(
      match => OUT_BASES[match[1].toLowerCase()] === base
    )
    found.push({ base, at, by: fielderIn(clause), thrown })
  }
  for (const match of transcript.matchAll(TAKES_BASE)) {
    const verb = match[1].toLowerCase()
    const after = transcript.slice(match.index + match[0].length)
    // Taking or getting a base is the runner's unless it is for the out.
    if (/^(?:takes?|took|gets?|got)$/.test(verb) && !FOR_THE_OUT.test(after))
      continue
    if (byRunner(match.index, match.index + match[0].length)) continue
    add(OUT_BASES[match[2].toLowerCase()], match.index)
  }
  for (const pattern of [BASE_FOR_OUT, FORCE_AT])
    for (const match of transcript.matchAll(pattern)) {
      // "To second base for the out" is where a ball went, not a base out.
      if (pattern === BASE_FOR_OUT && ballWentThere(transcript, match.index))
        continue
      add(OUT_BASES[match[1].toLowerCase()], match.index)
    }
  return found.sort((a, b) => a.at - b.at)
}

/**
 * The game as the ball in play at `ballAt` found it: the sentence's actions
 * before it applied — a steal, a caught stealing, an earlier batter's play.
 */
function gameAtBall(
  game: GameState,
  actions: readonly ScoringAction[],
  ballAt: number
): GameState {
  let state = game
  for (const action of actions.slice(0, Math.max(0, ballAt)))
    state = applyAction(state, action).state
  return state
}

/** Whether the ball in play at `ballAt` was caught: its out is the catch. */
function caughtAt(actions: readonly ScoringAction[], ballAt: number): boolean {
  return actions.some(
    (action, index) =>
      index > ballAt &&
      action.type === 'result' &&
      action.eventType === 'field_out' &&
      (action.outBy === 'catch' || action.foulCaught === true)
  )
}

/**
 * The base outs the words name, made the sentence's: the runner from the
 * base before it out there, credited to the fielder who took it. A fielder's
 * choice without the runner's out gets it; a ground out the words never put
 * the batter out on is a fielder's choice; a ball with no result whose base
 * out was a force is one too. A runner out the model sent there keeps its
 * chain; one sent with no fielders gets them.
 */
function baseOuts(
  game: GameState,
  actions: readonly ScoringAction[],
  transcript: string,
  said: readonly BaseOut[]
): ScoringAction[] {
  const ballAt = actions.findLastIndex(action => action.type === 'ballInPlay')
  if (said.length === 0 || ballAt < 0) return [...actions]
  // A caught ball's out is the catch: a base named with it is not a force.
  if (caughtAt(actions, ballAt)) return [...actions]
  const ball = actions[ballAt] as BallInPlayAction
  // The bases as the ball found them: a steal before it moved a runner.
  const atBall = gameAtBall(game, actions, ballAt)
  const defense = atBall.liveData.linescore.defense
  const offense = atBall.liveData.linescore.offense
  const on = [offense.first?.id, offense.second?.id, offense.third?.id]
  const at = (position: (typeof POSITIONS)[number]): number | undefined =>
    defense[position]?.id
  const sentence = [...actions]
  const outs: ScoringAction[] = []
  for (const out of said) {
    const from = FROM_INDEX[out.base]
    const runner = on[from]
    if (runner == null) continue
    // The model sent him safe there, or on past it: the words were his run.
    if (
      actions.some(
        (action, index) =>
          index > ballAt &&
          action.type === 'runnerMovement' &&
          action.runner === runner &&
          action.out !== true &&
          destinationBase(action.to) >= destinationBase(out.base)
      )
    )
      continue
    const forced = on.slice(0, from).every(id => id != null)
    const actor = out.by == null ? ball.fielder : at(out.by)
    const cover = actor == null ? undefined : coverAt(out.base, actor, at)
    const receiver = cover == null ? undefined : at(cover)
    const fielders =
      actor == null
        ? undefined
        : out.thrown && receiver != null && receiver !== actor
          ? [actor, receiver]
          : [actor]
    // A runner the model put out there who is not the one from the base
    // before it was its guess at this runner.
    const guessed = sentence.findIndex(
      action =>
        action.type === 'runnerMovement' &&
        action.out === true &&
        action.to === out.base &&
        action.runner !== runner &&
        action.runner !== batterOf(atBall)
    )
    if (guessed >= 0) sentence.splice(guessed, 1)
    const sent = sentence.findIndex(
      action =>
        action.type === 'runnerMovement' &&
        action.runner === runner &&
        action.out === true
    )
    if (sent >= 0) {
      const earlier = sentence[sent] as RunnerMovementAction
      if (earlier.to === out.base && earlier.fielders != null) continue
      sentence.splice(sent, 1)
    }
    outs.push({
      type: 'runnerMovement',
      runner,
      to: out.base,
      out: true,
      reason: forced ? 'Force Out' : 'Throw Out',
      ...(fielders == null ? {} : { fielders })
    })
  }
  if (outs.length === 0) return [...actions]
  // The second out of a double play the words put at second, unless the
  // model sent it.
  const second = DOUBLE_PLAY_AT.exec(transcript)
  const ownBall = sentence.indexOf(ball)
  if (
    second != null &&
    on[0] != null &&
    outs.every(out => (out as RunnerMovementAction).runner !== on[0]) &&
    !sentence.some(
      (action, index) =>
        index > ownBall &&
        action.type === 'runnerMovement' &&
        action.runner === on[0] &&
        action.out === true
    )
  ) {
    const first = (outs[0] as RunnerMovementAction).fielders?.at(-1)
    const cover = first == null ? undefined : coverAt('2B', first, at)
    const pivot = cover == null ? undefined : at(cover)
    outs.push({
      type: 'runnerMovement',
      runner: on[0],
      to: '2B',
      out: true,
      reason: 'Force Out',
      ...(first == null || pivot == null
        ? {}
        : { fielders: first === pivot ? [first] : [first, pivot] })
    })
  }
  const resultAt = sentence.findIndex(
    (action, index) => index > ballAt && action.type === 'result'
  )
  const result = resultAt < 0 ? undefined : (sentence[resultAt] as ResultAction)
  // An error on the play, with no wild throw after the out, is the base out
  // missed: "drops the ball for an error, and everyone is safe".
  if (result?.eventType === 'field_error' && !OVERTHROW.test(transcript))
    return [...actions]
  const forced = outs.some(
    out => out.type === 'runnerMovement' && out.reason === 'Force Out'
  )
  if (result == null) {
    if (!forced) return [...actions]
    sentence.splice(
      ownBall + 1,
      0,
      { type: 'result', eventType: 'fielders_choice' },
      ...outs
    )
    return sentence
  }
  if (
    result.eventType === 'field_out' &&
    result.outBy !== 'catch' &&
    result.foulCaught !== true &&
    !batterOutSaid(transcript)
  )
    sentence[resultAt] = { type: 'result', eventType: 'fielders_choice' }
  else if (
    result.eventType !== 'fielders_choice' &&
    result.eventType !== 'field_out' &&
    result.eventType !== 'field_error'
  )
    return [...actions]
  sentence.splice(resultAt + 1, 0, ...outs)
  return sentence
}

/**
 * An error the model charged before the result it belongs to goes right
 * after that result: the rules take an error only once a play is waiting for
 * it, as `field_error` leaves one.
 */
function errorAfterResult(actions: readonly ScoringAction[]): ScoringAction[] {
  const sentence = [...actions]
  const ballAt = sentence.findLastIndex(action => action.type === 'ballInPlay')
  if (ballAt < 0) return sentence
  const resultAt = sentence.findIndex(
    (action, index) =>
      index > ballAt &&
      action.type === 'result' &&
      action.eventType === 'field_error'
  )
  if (resultAt < 0) return sentence
  const early = sentence
    .slice(ballAt + 1, resultAt)
    .filter(action => action.type === 'fieldingPlay' && action.error != null)
  if (early.length === 0) return sentence
  const rest = sentence.filter(action => !early.includes(action))
  rest.splice(rest.indexOf(sentence[resultAt]) + 1, 0, ...early)
  return rest
}

/**
 * A base out followed by a wild throw to first, as one play in the one order
 * the rules take: the ball, the batter on the error, the error charged on
 * the throw to the first baseman, then the base out and the rest.
 */
function forceThenOverthrow(
  game: GameState,
  actions: readonly ScoringAction[],
  transcript: string,
  baseOutWords: readonly BaseOut[]
): ScoringAction[] {
  const wild = OVERTHROW.exec(transcript)
  if (wild == null) return [...actions]
  // Only a wild throw at or toward first: one the words send to another
  // base is no overthrow of the batter's.
  const toFirst = /\b(?:first|1st)\b/i.test(wild[0])
  const after = transcript
    .slice(wild.index + wild[0].length)
    .split(/[.;]/)[0]
    .slice(0, 40)
  if (!toFirst && /\b(?:second|2nd|third|3rd|home|the\s+plate)\b/i.test(after))
    return [...actions]
  // A throw the first baseman saved for the out was no error.
  if (
    SAVED_AT_FIRST.test(transcript.slice(wild.index)) ||
    doublePlaySaid(transcript) === 'done'
  )
    return [...actions]
  const said = baseOutWords.filter(out => out.at < wild.index)
  const ballAt = actions.findLastIndex(action => action.type === 'ballInPlay')
  // A caught ball's out is the catch, and a throw after it no error of the
  // batter's: a runner doubled off is no force.
  if (said.length === 0 || ballAt < 0 || caughtAt(actions, ballAt))
    return [...actions]
  const outAt = actions.findIndex(
    (action, index) =>
      index > ballAt &&
      action.type === 'runnerMovement' &&
      action.out === true &&
      action.to !== '1B' &&
      action.to !== 'batter' &&
      action.fielders != null &&
      action.fielders.length > 0
  )
  if (outAt < 0) return [...actions]
  const out = actions[outAt] as RunnerMovementAction
  const thrower = out.fielders!.at(-1)!
  const first = game.liveData.linescore.defense['1B']?.id
  const sent = actions.find(
    (action, index) =>
      index > ballAt &&
      action.type === 'fieldingPlay' &&
      action.error === thrower
  )
  // The model's own error on a throw to someone else stands as it sent it.
  const receiver =
    sent?.type === 'fieldingPlay' ? sent.fielders.at(-1) : undefined
  if (receiver != null && receiver !== thrower && receiver !== first)
    return [...actions]
  const error: ScoringAction = {
    ...(sent?.type === 'fieldingPlay' ? sent : {}),
    type: 'fieldingPlay',
    fielders: first == null || first === thrower ? [thrower] : [thrower, first],
    error: thrower,
    errorKind: 'throwing'
  }
  const rest = actions
    .slice(ballAt + 1)
    .filter(
      action =>
        action !== out &&
        action !== sent &&
        action.type !== 'result' &&
        !(action.type === 'fieldingPlay' && action.error === thrower)
    )
  return [
    ...actions.slice(0, ballAt + 1),
    { type: 'result', eventType: 'field_error' },
    error,
    out,
    ...rest
  ]
}

/** The words for each base a runner can be put out at. */
const BASE_WORDS: Partial<Record<Destination, string>> = {
  '1B': 'first|1st',
  '2B': 'second|2nd',
  '3B': 'third|3rd',
  home: 'home|the plate'
}

/** Words that say a run scored. */
const SCORES =
  /\b(?:scores?|scored|scoring|takes\s+home|comes\s+home|came\s+home|crosses|crossed)\b/gi
/**
 * Words that say a runner's out: not the batter's own "fly out" or "ground
 * out", which say where his ball went, nor a runner who "tags up" or "tags
 * and scores" on a catch.
 */
const OUT_WORD =
  /\b(?:(?<!\b(?:fly|flies|flied|ground|grounds|grounded|line|lines|lined|pop|pops|popped|strike|struck)\s)out|tag(?:s|ged)?\b(?!\s+(?:up|and)\b)|steps?\s+on|for\s+the\s+out)/gi
const CATCH_WORD = /\b(?:caught|catch(?:es)?|makes\s+the\s+catch)\b/i
/** Words that say an error: "error", or any wild throw `OVERTHROW` reads. */
const ERROR_WORD = new RegExp(`\\berror\\b|${OVERTHROW.source}`, 'i')
const THROW_OUT_AT_FIRST =
  /\b(?:thrown\s+out|throws?\s+him\s+out|out\s+at\s+first)\b/i
/** Words that say the ball was put in play: "grounder", "fly out", "single". */
const BALL_WORD =
  /\b(?:grounder|ground(?:ed|s)?\s+(?:ball|out|into)|bouncer|chopper|roller|dribbler|line\s*drive|liner|lines?\s+out|pop(?:s|ped)?[\s-]*(?:up|fly|out)|fly(?:\s+ball|\s+out)?|flies\s+out|bunt(?:s|ed)?|singles?|singled|doubles?(?!\s+(?:play|off))|doubled|triples?|tripled|home\s+run|homers?|homered)\b/i

/**
 * Where each action's words are in the sentence, as an index into it, or
 * undefined when its words cannot be found: a run by its scoring word nearest
 * after its runner's name, an out by its base or its out word, a catch, an
 * error, a ground out at first.
 */
function storyPositions(
  game: GameState,
  actions: readonly ScoringAction[],
  transcript: string,
  said: readonly BaseOut[]
): Array<number | undefined> {
  const scores = [...transcript.matchAll(SCORES)].map(match => match.index)
  const outsAt = [...transcript.matchAll(OUT_WORD)].map(match => match.index)
  let run = 0
  let outIndex = 0
  const named = (id: number): number | undefined => {
    for (const pattern of playerPatterns(game, id)) {
      const match = new RegExp(pattern, 'i').exec(transcript)
      if (match != null) return match.index
    }
    return undefined
  }
  // The ball where the words first put it in play; a sentence that never
  // says so is all about the ball.
  const ballWords = BALL_WORD.exec(transcript)?.index ?? 0
  return actions.map(action => {
    if (action.type === 'ballInPlay') return ballWords
    if (action.type === 'fieldingPlay')
      return action.error == null
        ? undefined
        : ERROR_WORD.exec(transcript)?.index
    if (action.type === 'result') {
      if (action.eventType === 'field_out')
        return action.outBy === 'catch' || action.foulCaught === true
          ? CATCH_WORD.exec(transcript)?.index
          : THROW_OUT_AT_FIRST.exec(transcript)?.index
      if (action.eventType === 'field_error')
        return ERROR_WORD.exec(transcript)?.index
      return undefined
    }
    if (action.type !== 'runnerMovement') return undefined
    const name = named(action.runner)
    if (action.out === true) {
      const base = said.find(out => out.base === action.to)
      if (base != null) return base.at
      // "Thrown out at second": the out the words put at his base.
      const words = BASE_WORDS[action.to]
      const atBase =
        words == null
          ? undefined
          : new RegExp(
              `\\b(?:out|tagged|doubled\\s+off|picked\\s+off)\\b[^.;]{0,30}?\\bat\\s+(?:the\\s+)?(?:${words})\\b`,
              'i'
            ).exec(transcript)?.index
      if (atBase != null) return atBase
      const after = name == null ? undefined : outsAt.find(at => at > name)
      return after ?? outsAt[outIndex++]
    }
    if (action.to === 'home') {
      const after = name == null ? undefined : scores.find(at => at > name)
      return after ?? scores[run++]
    }
    return name
  })
}

/** What the third out leaves of a sentence, and what it left out. */
export interface SentenceDefaults {
  actions: ScoringAction[]
  leftOut: string[]
}

/**
 * Nothing in a sentence happens after the play's third out. Outs are counted
 * from the game's, in the order the words tell the play. After the third out
 * a runner's run or advance, an error charged on a later throw and a further
 * out are left out, and listed. A batter's own third out before he reached
 * first takes every run from his ball in play on (rule 5.08(a)(1)); only a
 * runner's third out keeps a run that crossed before it, and a run the words
 * put first is moved ahead of that out. A result whose only error was left
 * out is the force's fielder's choice or the batter's out, and a ball with no
 * result whose third out was a force is a fielder's choice.
 */
function thirdOut(
  game: GameState,
  actions: readonly ScoringAction[],
  transcript: string,
  said: readonly BaseOut[]
): SentenceDefaults {
  const ballAt = actions.findLastIndex(action => action.type === 'ballInPlay')
  const play = [...actions]
  // The game as the ball found it: what the sentence did before it — a
  // caught stealing, a pickoff, an earlier batter's play — has its outs.
  const atBall = gameAtBall(game, play, ballAt)
  const batter = atBall.liveData.plays.currentPlay?.matchup.batter.id
  // A fielder's choice's calls describe one defensive sequence, not its
  // order, and the rules already take a crossing after its third out.
  if (
    play.some(
      (action, index) =>
        index > ballAt &&
        action.type === 'result' &&
        action.eventType === 'fielders_choice'
    )
  )
    return { actions: [...actions], leftOut: [] }
  // The order the play's outs came in: a catch first, then the forces and
  // any runner put out before the throw that got the batter, then the
  // batter's out at first, and a runner thrown out after it last. The batter's out said twice — a ground
  // out and his out at first — is one out.
  // A force: a runner put out at the next base with every base behind him
  // taken when the play began, whatever reason the call gave. A caught ball
  // forces nobody: its batter never ran.
  const offense = atBall.liveData.linescore.offense
  const start = [offense.first?.id, offense.second?.id, offense.third?.id]
  // The base the batter's reaching forces each runner to: his own when he
  // is not forced, the next one free of the batter and the runners behind
  // him when he is.
  const playResult = play.find(
    (action, index): action is ResultAction =>
      index > ballAt && action.type === 'result'
  )
  const reaches: Partial<Record<string, number>> = {
    single: 1,
    walk: 1,
    intent_walk: 1,
    hit_by_pitch: 1,
    field_error: 1,
    fielders_choice: 1,
    double: 2,
    triple: 3,
    home_run: 4
  }
  const batterReaches = reaches[playResult?.eventType ?? ''] ?? 0
  const forcedBases = new Map<number, number>()
  let reach = batterReaches
  start.forEach((id, index) => {
    if (id == null) return
    const base = index + 1
    const to = reach >= base ? reach + 1 : base
    forcedBases.set(id, to)
    reach = reach >= base ? to : reach
  })
  const forcedTo = (runner: number): number =>
    forcedBases.get(runner) ?? Math.max(0, start.indexOf(runner) + 1)
  const began = game.liveData.linescore.offense
  const onBase = new Set(
    [...start, began.first?.id, began.second?.id, began.third?.id].filter(
      (id): id is number => id != null
    )
  )
  const caughtBall = caughtAt(play, ballAt)
  const isForce = (action: ScoringAction): boolean => {
    if (action.type !== 'runnerMovement' || action.out !== true) return false
    if (caughtBall) return false
    if (action.reason === 'Force Out') return true
    const from = start.indexOf(action.runner)
    return (
      from >= 0 &&
      destinationBase(action.to) === from + 2 &&
      start.slice(0, from).every(id => id != null)
    )
  }
  const isBatterAtFirst = (action: ScoringAction): boolean =>
    action.type === 'runnerMovement' &&
    action.runner === batter &&
    (action.to === '1B' || action.to === 'batter')
  // Who threw the batter out, and who took the throw: his out's fielders,
  // else the fielder the ball went to and the first baseman. A runner out
  // that ends with the thrower holding the ball came first, and the throw to
  // first was its relay; one thrown on from first came after.
  const batterOutSent = play.find(
    (action, index): action is RunnerMovementAction =>
      index > ballAt &&
      action.type === 'runnerMovement' &&
      action.out === true &&
      isBatterAtFirst(action)
  )
  const ball = play[ballAt]
  const batterThrower =
    batterOutSent?.fielders?.[0] ??
    (ball?.type === 'ballInPlay' ? ball.fielder : undefined)
  const batterReceiver =
    batterOutSent?.fielders?.at(-1) ??
    atBall.liveData.linescore.defense['1B']?.id
  const beforeBatter = (action: RunnerMovementAction): boolean =>
    batterThrower != null &&
    action.fielders?.at(-1) === batterThrower &&
    !(action.fielders.length > 1 && action.fielders[0] === batterReceiver)
  const rank = (action: ScoringAction): number | undefined => {
    if (action.type === 'result' && action.eventType === 'field_out')
      return action.outBy === 'catch' || action.foulCaught === true ? 0 : 2
    if (action.type === 'runnerMovement' && action.out === true)
      return isBatterAtFirst(action)
        ? 2
        : isForce(action) || beforeBatter(action)
          ? 1
          : 3
    return undefined
  }
  const outs = play
    .flatMap((action, index) => {
      const order = index < ballAt ? undefined : rank(action)
      return order == null ? [] : [{ index, order }]
    })
    .sort((a, b) =>
      a.order === b.order ? a.index - b.index : a.order - b.order
    )
  let count = atBall.liveData.linescore.outs
  let batterOut = false
  let third: number | undefined
  for (const { index } of outs) {
    const action = play[index]
    const batters = action.type === 'result' || isBatterAtFirst(action)
    if (batters && batterOut) continue
    if (batters) batterOut = true
    count++
    if (count >= 3) {
      third = index
      break
    }
  }
  if (third == null || ballAt < 0) return { actions: [...actions], leftOut: [] }
  const ending = play[third]
  // The batter's own out before he reached first, or a force: no run on the
  // play counts (rule 5.08(a)). Any other runner's third out keeps a run
  // that crossed before it, and the calls are put in that order.
  const byBatter = ending.type === 'result' || isBatterAtFirst(ending)
  const forced = isForce(ending)
  const voids = byBatter || forced
  const afterThird = new Set(
    outs
      .slice(outs.findIndex(out => out.index === third) + 1)
      .map(out => play[out.index])
  )
  // Where the words put an error and the third out: an error said after it
  // was on a throw the inning had already ended.
  const raw = storyPositions(game, play, transcript, said)
  const thirdAt = raw[third]
  const leftOut: string[] = []
  const dropped = new Set<ScoringAction>()
  // Runners whose move the words put after a runner's third out: they hold,
  // or the play's defaults would move them anyway.
  const held: Array<{ runner: number; to: number }> = []
  play.forEach((action, index) => {
    if (index === third) return
    if (action.type === 'runnerMovement' && action.out !== true) {
      const run = action.to === 'home'
      // A batter's runs before his ball in play are voided only when the
      // words put them after it.
      const onPlay =
        index > ballAt ||
        (raw[index] != null && raw[index] > (raw[ballAt] ?? 0))
      // Any other third out keeps what crossed before it; what the words
      // put after it never happened.
      // A runner the batter forces on goes at least that far, whatever was
      // said after the out.
      const saidAfter =
        !voids &&
        onPlay &&
        thirdAt != null &&
        raw[index] != null &&
        raw[index] > thirdAt &&
        destinationBase(action.to) > forcedTo(action.runner)
      const voided = (voids && onPlay && (run || index > third)) || saidAfter
      if (saidAfter)
        held.push({ runner: action.runner, to: destinationBase(action.to) })
      if (voided) {
        dropped.add(action)
        // Only a runner the sentence could move: one on base when it, or
        // the play, began, or the batter when he was not out on it.
        if (
          onBase.has(action.runner) ||
          (action.runner === batter && !batterOut)
        )
          leftOut.push(
            `${nameOf(game, action.runner)} ${run ? 'scoring' : `to ${SPOKEN[action.to]}`}`
          )
      }
    } else if (action.type === 'runnerMovement' && afterThird.has(action)) {
      // The batter's out said twice is the one out, and is kept.
      if (!isBatterAtFirst(action)) {
        dropped.add(action)
        leftOut.push(
          `${nameOf(game, action.runner)} out at ${SPOKEN[action.to]}`
        )
      }
    } else if (
      action.type === 'fieldingPlay' &&
      action.error != null &&
      index > ballAt &&
      thirdAt != null &&
      raw[index] != null &&
      raw[index] > thirdAt
    ) {
      dropped.add(action)
      leftOut.push(`the error by ${nameOf(game, action.error)}`)
    }
  })
  let kept = play.filter(action => !dropped.has(action))
  // Where the play's own moves leave each runner once its result is in:
  // the defaults keep him there unless something moves him on.
  const keptBall = kept.indexOf(play[ballAt])
  const keptResult = kept.findIndex(
    (action, index) => index > keptBall && action.type === 'result'
  )
  let atResult = atBall
  for (const action of keptResult < 0
    ? []
    : kept.slice(keptBall, keptResult + 1))
    atResult = applyAction(atResult, action).state
  const holds = held.flatMap(({ runner, to }): ScoringAction[] => {
    // His own move in the words before the out already settles him.
    if (
      kept.some(
        action =>
          action.type === 'runnerMovement' &&
          action.runner === runner &&
          action.out !== true
      )
    )
      return []
    const left =
      atResult.pending?.movements.find(move => move.playerId === runner)?.to ??
      forcedTo(runner)
    // Held only where the defaults would carry him as far as the move left
    // out: one base short of it, and never short of where he is forced.
    if (left < to) return []
    const base = Math.max(forcedTo(runner), to - 1)
    return base < 1 || base > 3
      ? []
      : [{ type: 'runnerMovement', runner, to: baseDestination(base) }]
  })
  if (holds.length > 0) kept.splice(kept.indexOf(ending), 0, ...holds)
  // A catch is the batter's out, before any runner's: sent after them, it
  // goes right after its ball.
  const caught = kept.find(
    (action, index) =>
      index > kept.indexOf(play[ballAt]) &&
      action.type === 'result' &&
      action.eventType === 'field_out' &&
      (action.outBy === 'catch' || action.foulCaught === true)
  )
  if (caught != null) {
    kept = kept.filter(action => action !== caught)
    kept.splice(kept.indexOf(play[ballAt]) + 1, 0, caught)
  }
  // A runner's third out keeps the runs and advances of the play ahead of
  // it, never ahead of the play's result.
  if (!voids) {
    const resultAt = kept.findIndex(
      (action, index) =>
        index > kept.indexOf(play[ballAt]) && action.type === 'result'
    )
    const ahead = kept.filter(
      action =>
        action.type === 'runnerMovement' &&
        action.out !== true &&
        kept.indexOf(action) > kept.indexOf(ending)
    )
    if (ahead.length > 0) {
      kept = kept.filter(action => !ahead.includes(action))
      const at = Math.max(kept.indexOf(ending), resultAt + 1)
      kept.splice(at, 0, ...ahead)
    }
  }
  const errors = kept.some(
    action => action.type === 'fieldingPlay' && action.error != null
  )
  const resultAt = kept.findIndex(
    (action, index) =>
      index > kept.indexOf(play[ballAt]) && action.type === 'result'
  )
  const result = resultAt < 0 ? undefined : (kept[resultAt] as ResultAction)
  if (
    result?.eventType === 'field_error' &&
    !errors &&
    [...dropped].some(action => action.type === 'fieldingPlay')
  )
    kept[resultAt] = forced
      ? { type: 'result', eventType: 'fielders_choice' }
      : { type: 'result', eventType: 'field_out', outBy: 'throw' }
  else if (result == null && forced)
    kept.splice(kept.indexOf(play[ballAt]) + 1, 0, {
      type: 'result',
      eventType: 'fielders_choice'
    })
  return { actions: kept, leftOut }
}

/** Explicit base-by-base FC calls take precedence over a partial model reply. */
function explicitChoice(
  game: GameState,
  actions: ScoringAction[],
  words: string
): ScoringAction[] {
  if (
    !/\bfielder['’]?s? choice\b/i.test(words) ||
    /\b(?:not|never|almost|nearly|if|\w+n['’]t)\b/i.test(words) ||
    /\bto (?:the )?(?:first|second|third|home|plate)\b(?!\s+baseman)/i.test(
      words
    ) ||
    actions.some(
      a =>
        a.type === 'runnerMovement' &&
        ((a.runner === game.liveData.plays.currentPlay?.matchup.batter.id &&
          (a.out === true || destinationBase(a.to) > 1)) ||
          (a.out !== true &&
            a.runner !== game.liveData.plays.currentPlay?.matchup.batter.id &&
            (a.fielders?.length ?? 0) > 0) ||
          a.locations != null ||
          a.runnerLocation != null ||
          a.fieldingRoute != null ||
          actions.filter(
            other =>
              other.type === 'runnerMovement' && other.runner === a.runner
          ).length > 1)
    ) ||
    actions.filter(a => a.type === 'ballInPlay').length !== 1 ||
    actions.filter(a => a.type === 'result').length !== 1 ||
    actions.some(
      a => !['ballInPlay', 'result', 'runnerMovement'].includes(a.type)
    )
  )
    return actions
  const ball = actions.find(
    (a): a is BallInPlayAction => a.type === 'ballInPlay'
  )!
  const batter = game.liveData.plays.currentPlay?.matchup.batter.id
  const on = [
    game.liveData.linescore.offense.first,
    game.liveData.linescore.offense.second,
    game.liveData.linescore.offense.third
  ]
  if (batter == null || ball.fielder == null || on.every(r => r == null))
    return actions
  const names = ['first', 'second', 'third']
  const calls: Array<{
    runner: number
    from: number
    to: Destination
    out: boolean
    clause: string
  }> = []
  for (const clause of words.split(/[.;,]|\b(?:then|and|but)\b/i)) {
    const from = /\brunner from (first|second|third)\b/i.exec(clause)
    if (from == null) continue
    const base = names.indexOf(from[1].toLowerCase())
    const runner = on[base]?.id
    if (runner == null || calls.some(c => c.runner === runner)) continue
    const isOut =
      /\bout\b|\bretire[sd]?\b/i.test(clause) &&
      !/\b(?:not out|safe)\b/i.test(clause)
    if (/\b(?:not|never|almost|nearly)\b/i.test(clause)) return actions
    const isSafe = /\bsafe\b|\bscores?\b/i.test(clause)
    if (!isOut && !isSafe) continue
    const target = /\b(?:at|on) (first|second|third|home)\b/i
      .exec(clause)?.[1]
      ?.toLowerCase()
    const to =
      target === 'home' || /\bscores?\b/i.test(clause)
        ? 'home'
        : target == null
          ? BASE_NAMES[base + 2]
          : BASE_NAMES[names.indexOf(target) + 1]
    calls.push({ runner, from: base + 1, to, out: isOut, clause })
  }
  if (/\ball runners are safe\b/i.test(words))
    for (let base = 2; base >= 0; base--) {
      const runner = on[base]?.id
      if (runner != null && !calls.some(c => c.runner === runner))
        calls.push({
          runner,
          from: base + 1,
          to: BASE_NAMES[base + 2],
          out: false,
          clause: ''
        })
    }
  if (
    calls.length !== on.filter(r => r != null).length &&
    calls.filter(c => c.out).length < 3 - game.liveData.linescore.outs
  )
    return actions
  const idOf = (position: (typeof POSITIONS)[number]): number | undefined =>
    game.liveData.linescore.defense[position]?.id
  const route = chainOf([ball.fielder, ...receiversSaid(words).all.map(idOf)])
  let cursor = 0
  let uncertain = false
  const moves: RunnerMovementAction[] = calls.map(call => {
    if (!call.out)
      return { type: 'runnerMovement', runner: call.runner, to: call.to }
    const holder = route[cursor] ?? ball.fielder!
    const receiver =
      namedAt(game, call.clause, call.to, holder) ??
      (HIMSELF.test(call.clause) ? holder : undefined)
    const supplied = actions.find(
      (a): a is RunnerMovementAction =>
        a.type === 'runnerMovement' &&
        a.runner === call.runner &&
        a.out === true &&
        destinationBase(a.to) === destinationBase(call.to)
    )
    if (
      supplied?.fielders != null &&
      supplied.fielders.length > 0 &&
      (receiver == null || supplied.fielders.at(-1) === receiver)
    ) {
      const last = supplied.fielders.at(-1)
      cursor = Math.max(
        cursor,
        route.findIndex((id, i) => i >= cursor && id === last)
      )
      return { ...supplied }
    }
    if (receiver == null) uncertain = true
    const end =
      receiver == null
        ? cursor
        : route.findIndex((id, i) => i >= cursor && id === receiver)
    const fielders =
      end < cursor ? [holder, receiver!] : route.slice(cursor, end + 1)
    cursor = Math.max(cursor, end)
    return {
      type: 'runnerMovement',
      runner: call.runner,
      to: call.to,
      out: true,
      fielders
    }
  })
  if (uncertain) return actions
  const holder =
    moves.findLast(move => move.out === true)?.fielders?.at(-1) ?? ball.fielder
  const suppliedSafe = actions.find(
    (a): a is RunnerMovementAction =>
      a.type === 'runnerMovement' &&
      a.runner === batter &&
      a.out !== true &&
      (a.fielders?.length ?? 0) > 1
  )
  const remaining =
    suppliedSafe?.fielders?.[0] === holder
      ? suppliedSafe.fielders
      : chainOf([holder, ...route.slice(cursor + 1)])
  return [
    ball,
    { type: 'result', eventType: 'fielders_choice' },
    {
      type: 'runnerMovement',
      runner: batter,
      to: '1B',
      ...(remaining.length > 1 ? { fielders: remaining } : {})
    },
    ...moves
  ]
}

/** Spoken Safe relays carry throws without awarding putout/assist credits. */
function choiceRelayLocations(
  game: GameState,
  actions: ScoringAction[]
): ScoringAction[] {
  const located = [...actions]
  const starts = actions.flatMap((a, index) =>
    a.type === 'result' && a.eventType === 'fielders_choice' ? [index] : []
  )
  if (game.pending?.outcome === "Fielder's Choice") starts.unshift(-1)
  for (const start of starts) {
    const boundary = actions.findIndex(
      (a, index) =>
        index > start &&
        ['pitch', 'result', 'ballInPlay', 'gameFlow', 'lineup'].includes(a.type)
    )
    const end = boundary < 0 ? actions.length : boundary
    const ball = actions
      .slice(0, Math.max(0, start))
      .findLast((a): a is BallInPlayAction => a.type === 'ballInPlay')
    const players =
      ball == null
        ? scoringFieldPlayers({
            session: game,
            phase: 'ready',
            pendingPlay: null,
            motions: [],
            runnerAction: null
          })
        : fieldPlayersFor(game)
    const positions = new Map(
      players
        .filter(p => p.team === 'defense')
        .map(p => [p.playerId, p.position])
    )
    if (ball?.fielder != null) {
      const point = ball.location ?? positions.get(ball.fielder)
      if (point != null)
        positions.set(ball.fielder, fieldingLocation("Fielder's Choice", point))
    }
    const moves = actions.flatMap((a, index) =>
      index > start && index < end && a.type === 'runnerMovement'
        ? [{ action: a, index }]
        : []
    )
    // The reel shows putouts first, followed by all late Safe relay segments.
    const ordered = [
      ...moves.filter(m => m.action.out === true),
      ...moves.filter(m => m.action.out !== true)
    ]
    for (const { action, index } of ordered) {
      if (action.fielders == null) continue
      const locations =
        action.locations ??
        action.fielders.map((id, hop) => {
          if (action.out === true)
            return hop === action.fielders!.length - 1
              ? (action.runnerLocation ?? BASES[destinationBase(action.to) % 4])
              : (positions.get(id) ?? BASES[0])
          if (hop === 0) return positions.get(id) ?? BASES[0]
          const position = POSITIONS.find(
            p => game.liveData.linescore.defense[p]?.id === id
          )
          const base =
            position === '1B'
              ? 1
              : position === '2B' || position === 'SS'
                ? 2
                : position === '3B'
                  ? 3
                  : position === 'C'
                    ? 0
                    : undefined
          return base == null ? (positions.get(id) ?? BASES[0]) : BASES[base]
        })
      for (const [hop, id] of action.fielders.entries())
        positions.set(id, locations[hop])
      located[index] = {
        ...action,
        locations: locations.map(point => ({ ...point }))
      }
    }
    const relays = ordered.filter(
      m => m.action.out !== true && (m.action.fielders?.length ?? 0) > 1
    )
    if (relays.length === 0) continue
    const earlier = start < 0 ? scoringDefensiveRoute(game) : []
    const route = [
      ...earlier.map(hop => ({
        id: hop.player.id,
        point: { ...hop.location }
      })),
      ...ordered
        .filter(m => m.action.out === true || relays.includes(m))
        .flatMap(({ index }) => {
          const action = located[index] as RunnerMovementAction
          return (action.fielders ?? []).map((id, hop) => ({
            id,
            point: { ...action.locations![hop] }
          }))
        })
    ]
    for (const { index } of moves)
      located[index] = {
        ...(located[index] as RunnerMovementAction),
        fieldingRoute: route.map(hop => ({
          id: hop.id,
          point: { ...hop.point }
        }))
      }
  }
  return located
}

/** Where a ground ball or a bunt was hit, said as "to third" or "to short". */
const GROUND_WORD =
  'ground(?:er| ?ball)?|grabber|chopper|roller|hopper|bunt(?:s|ed)?|sacrifice(?: bunt)?|sac bunt|tapper|tapped'
const GROUND_AT: Record<string, (typeof POSITIONS)[number]> = {
  shortstop: 'SS',
  short: 'SS',
  pitcher: 'P',
  catcher: 'C',
  'first baseman': '1B',
  'second baseman': '2B',
  'third baseman': '3B',
  first: '1B',
  second: '2B',
  third: '3B'
}

/**
 * The infielder a ground ball was hit to, from the words. A throw's "to
 * first" and a runner's "to third" are not where the ball was hit.
 */
function groundFielder(
  transcript: string
): (typeof POSITIONS)[number] | undefined {
  const match = new RegExp(
    `\\b(?:${GROUND_WORD})\\b(?:(?!\\b(?:and|but|he|she|they|runner|throw|thrown|throws|tags?|tagged|goes|went|out|safe)\\b)[^.;]){0,40}?\\b(?:to|toward|towards)\\s+(?:the\\s+)?(third baseman|second baseman|first baseman|shortstop|pitcher|catcher|short|first|second|third)(?:\\s+base)?\\b`,
    'i'
  ).exec(transcript)
  const word = match?.[1]?.toLowerCase()
  return word == null ? undefined : GROUND_AT[word]
}

/** Whether the words name this fielder, by name or by his position. */
function namedFielder(
  game: GameState,
  transcript: string,
  id: number
): boolean {
  if (namesPlayer(game, id, transcript)) return true
  const defense = game.liveData.linescore.defense
  const position = POSITIONS.find(place => defense[place]?.id === id)
  const noun = FIELDER_NOUNS.find(([, place]) => place === position)?.[0]
  return noun != null && new RegExp(`\\b(?:${noun})\\b`, 'i').test(transcript)
}

/**
 * The fielder the words say took the throw at this base: a position they
 * said the throw went to, a player they named there, or a shortstop or
 * second baseman they said threw on from second.
 */
function namedAt(
  game: GameState,
  transcript: string,
  to: Destination,
  thrower: number
): number | undefined {
  const defense = game.liveData.linescore.defense
  const idOf = (position: (typeof POSITIONS)[number]): number | undefined =>
    defense[position]?.id
  const legal = (position: (typeof POSITIONS)[number]): boolean =>
    to === '2B'
      ? position === '2B' || position === 'SS'
      : to === 'home'
        ? position === 'C'
        : to === '3B'
          ? position === '3B'
          : to === '1B'
            ? position === '1B' || position === 'P'
            : false
  const said = receiversSaid(transcript).between.find(
    position => legal(position) && idOf(position) !== thrower
  )
  if (said != null && idOf(said) != null) return idOf(said)
  for (const [noun, position] of FIELDER_NOUNS) {
    if (!legal(position) || idOf(position) === thrower) continue
    if (
      new RegExp(`\\b(?:to|by)\\s+(?:the\\s+)?(?:${noun})\\b`, 'i').test(
        transcript
      )
    )
      return idOf(position)
  }
  for (const position of POSITIONS) {
    if (!legal(position)) continue
    const id = idOf(position)
    if (id == null || id === thrower || !namesPlayer(game, id, transcript))
      continue
    const addressed = playerPatterns(game, id).some(pattern =>
      new RegExp(
        `\\b(?:to|by)\\s+(?:\\w+\\s+){0,3}?${pattern}|${pattern}(?:'s)?\\s+throw`,
        'i'
      ).test(transcript)
    )
    if (addressed) return id
  }
  return undefined
}

/**
 * A throw-out whose fielders were left off: the fielder the ball went to,
 * then the one who covers the base. A chain already sent is kept.
 */
function creditThrowOuts(
  game: GameState,
  actions: readonly ScoringAction[],
  transcript: string
): ScoringAction[] {
  const defense = game.liveData.linescore.defense
  const idOf = (position: (typeof POSITIONS)[number]): number | undefined =>
    defense[position]?.id
  const positions = new Map(
    scoringFieldPlayers({
      session: game,
      phase: 'ready',
      pendingPlay: null,
      motions: [],
      runnerAction: null
    })
      .filter(p => p.team === 'defense')
      .map(p => [p.playerId, p.position])
  )
  let thrower: number | undefined =
    game.pending?.outcome === "Fielder's Choice"
      ? (scoringDefensiveRoute(game).at(-1)?.player.id ?? game.pending.fielder)
      : undefined
  const withFielders = (
    action: RunnerMovementAction,
    fielders: number[]
  ): RunnerMovementAction => ({
    ...action,
    fielders,
    ...(action.locations?.length === 1 && fielders.length === 2
      ? {
          locations: [
            positions.get(fielders[0]) ?? BASES[0],
            ...action.locations
          ]
        }
      : {})
  })
  const remember = (action: RunnerMovementAction): void => {
    for (const [index, id] of (action.fielders ?? []).entries()) {
      const point =
        action.locations?.[index] ??
        (index === action.fielders!.length - 1
          ? (action.runnerLocation ?? BASES[destinationBase(action.to) % 4])
          : positions.get(id))
      if (point != null) positions.set(id, point)
    }
  }
  return actions.map((action, index) => {
    if (action.type === 'ballInPlay') {
      const onGround =
        action.trajectory === 'ground_ball' ||
        action.trajectory === 'chopper' ||
        action.contact === 'bunt'
      thrower =
        action.fielder ??
        (!onGround && action.direction != null
          ? idOf(OUTFIELDER[action.direction])
          : undefined)
      positions.clear()
      for (const player of fieldPlayersFor(game))
        if (player.team === 'defense')
          positions.set(player.playerId, player.position)
      if (thrower != null) {
        const point = action.location ?? positions.get(thrower)
        if (point != null) {
          const following = actions.slice(index + 1)
          const nextBall = following.findIndex(a => a.type === 'ballInPlay')
          const result = following
            .slice(0, nextBall < 0 ? following.length : nextBall)
            .find(a => a.type === 'result')
          const fair =
            result?.type === 'result' &&
            [
              'single',
              'double',
              'triple',
              'field_error',
              'fielders_choice'
            ].includes(result.eventType)
          positions.set(
            thrower,
            fieldingLocation(fair ? "Fielder's Choice" : 'Throw Out', point)
          )
        }
      }
      return action
    }
    if (action.type !== 'runnerMovement' || action.out !== true) return action
    if ((action.fielders?.length ?? 0) > 0) {
      const fielders = action.fielders!
      const receiver = fielders.at(-1)!
      const onlyReceiver =
        fielders.length === 1 &&
        thrower != null &&
        receiver !== thrower &&
        namedAt(game, transcript, action.to, thrower) === receiver
      const credited = onlyReceiver
        ? withFielders(action, [thrower!, receiver])
        : action
      remember(credited)
      thrower = receiver
      return credited
    }
    if (thrower == null) return action
    const named = namedAt(game, transcript, action.to, thrower)
    const cover = coverAt(action.to, thrower, idOf)
    const receiver = named ?? (cover == null ? undefined : idOf(cover))
    if (receiver == null) return action
    const from = thrower
    thrower = receiver
    const credited = withFielders(
      action,
      receiver === from ? [from] : [from, receiver]
    )
    remember(credited)
    return credited
  })
}

/** Pitches that are a strike whatever the count; a foul only below two. */
const STRIKES: ReadonlySet<string> = new Set(['C', 'S', 'T', 'L'])

/**
 * An out sent straight after strike three, with no ball in play of its own
 * ("foul ball, swing and a miss, called strike three" sent with a caught foul
 * or a strikeout after it): the at-bat ended with the strikeout, and the out
 * is the model's invention, not the next batter's.
 */
function afterStrikeThree(
  game: GameState,
  actions: readonly ScoringAction[],
  transcript: string
): ScoringAction[] {
  let strikes = game.liveData.linescore.strikes
  let struckOut = false
  const kept: ScoringAction[] = []
  for (const action of actions) {
    if (action.type === 'pitch') {
      if (STRIKES.has(action.call) || (action.call === 'F' && strikes < 2))
        strikes++
      struckOut = strikes >= 3
      kept.push(action)
      continue
    }
    const after = struckOut && action.type === 'result'
    struckOut = false
    if (action.type === 'result' || action.type === 'ballInPlay') strikes = 0
    if (after && action.eventType === 'strikeout') {
      // A strikeout said again is invented too, the pitch made it — unless
      // the third strike was dropped: then the result is the dropped third
      // strike, and the pitch is what goes, so the rules ask whether he
      // reached.
      if (DROPPED_THIRD.test(transcript)) kept.splice(-1, 1, action)
      continue
    }
    if (after && BATTED.has(action.eventType)) continue
    kept.push(action)
  }
  return kept
}

/** Words that say the third strike got away from the catcher. */
const DROPPED_THIRD =
  /\b(?:drop(?:s|ped)?|in the dirt|gets? away|got away|passed ball|wild pitch|reach(?:es|ed)?|runs? to first|beats? (?:out )?(?:the )?throw)\b/i

/** Each runner's base as the sentence begins. */
function basesOf(game: GameState): Map<number, Destination> {
  const offense = game.liveData.linescore.offense
  const on = new Map<number, Destination>()
  if (offense.first != null) on.set(offense.first.id, '1B')
  if (offense.second != null) on.set(offense.second.id, '2B')
  if (offense.third != null) on.set(offense.third.id, '3B')
  return on
}

/**
 * A runner sent, safe, to the base he is already on held there ("the runner
 * holds"). When the play has moved him on by rule — home from third on a
 * single — the move keeps him there; when it has not, there is nothing to
 * move, and the rules would refuse it.
 */
function holding(
  game: GameState,
  actions: readonly ScoringAction[]
): ScoringAction[] {
  const on = basesOf(game)
  const stays = (action: ScoringAction, index: number): boolean =>
    action.type === 'runnerMovement' &&
    action.out !== true &&
    on.get(action.runner) === action.to &&
    !actions
      .slice(0, index)
      .some(
        earlier =>
          earlier.type === 'runnerMovement' && earlier.runner === action.runner
      )
  // A hold said before the play's result holds him against that result.
  const resultAt = actions.findIndex(action => action.type === 'result')
  const ordered =
    resultAt < 0
      ? [...actions]
      : [
          ...actions.slice(0, resultAt).filter((a, i) => !stays(a, i)),
          actions[resultAt],
          ...actions.slice(0, resultAt).filter((a, i) => stays(a, i)),
          ...actions.slice(resultAt + 1)
        ]
  const moved = new Set<number>()
  let state = game
  const kept: ScoringAction[] = []
  for (const action of ordered) {
    if (action.type === 'runnerMovement') {
      const stays =
        action.out !== true &&
        action.fieldingRoute == null &&
        !moved.has(action.runner) &&
        on.get(action.runner) === action.to
      moved.add(action.runner)
      const sent = state.pending?.movements.find(
        move => move.playerId === action.runner
      )
      if (stays && (sent == null || sent.to === destinationBase(action.to)))
        continue
    }
    kept.push(action)
    state = applyAction(state, action).state
  }
  return kept
}

const DOUBLE_PLAY =
  /\b(?:double[- ]?plays?|d\.? ?p\.?|turns? two|turned two|turning two|twin killing)\b/gi
/**
 * Words just before a double play that say it did not happen: "no double
 * play", "they couldn't turn two", "they try to turn two".
 */
const NOT_DONE =
  /\b(?:no|not|never|couldn[’']t|could not|can[’']t|cannot|didn[’']t|did not|don[’']t|doesn[’']t|wasn[’']t|failed to|fails? to|almost|nearly|too late|late)(?:\s+(?:a|an|the|get|gets|got|turn|make|complete|for|chance|time|in|enough|quite))*\s*$/i
/**
 * Words just before a double play that only try for it — "they try to turn
 * two", "they go for the double play" — which the words after it say failed
 * ("but the relay is late", "but only get the lead runner").
 */
const TRIED =
  /\b(?:tr(?:y|ies|ied|ying)|attempt(?:s|ed|ing)?|go(?:es|ing)?|went)\s+(?:to|for)(?:\s+(?:a|an|the|get|turn))*\s*$/i
const FELL_SHORT =
  /^[^.]*?\bbut\b[^.]*?\b(?:late|only|just|beats?|miss(?:es|ed)?|in time|batter (?:is |was )?safe|safe at first)\b/i
/**
 * Words beside a double play's that say the batter was safe at first — not
 * that he almost was, and not a runner beating a throw elsewhere.
 */
const BATTER_SAFE =
  /(?<!\b(?:almost|nearly|just about)\s+)\b(?:batter (?:is |was )?safe|safe at first|beats? it out|beats? (?:out )?(?:the )?(?:throw|relay|return)(?!\s+(?:home|to (?:the plate|home|second|third)|at (?:home|the plate|second|third))))\b/i
/** A double play off a ball in the air: the rules' own, not this one. */
const IN_THE_AIR =
  /\b(?:line drive|liner|lines (?:out|into|to)|lined (?:out|into|to)|fly ball|fly out|flies|flied|pop ?ups?|pop fly|pops? (?:up|out to)|popped (?:up|out)|doubled?(?:\s+\w+){0,3}\s+off|doubles(?:\s+\w+){0,3}\s+off)\b/i
const POSITION_WORDS: Record<string, (typeof POSITIONS)[number]> = {
  1: 'P',
  2: 'C',
  3: '1B',
  4: '2B',
  5: '3B',
  6: 'SS',
  7: 'LF',
  8: 'CF',
  9: 'RF',
  one: 'P',
  two: 'C',
  three: '1B',
  four: '2B',
  five: '3B',
  six: 'SS',
  seven: 'LF',
  eight: 'CF',
  nine: 'RF'
}
const POSITION_TOKEN = '(?:[1-9]|one|two|three|four|five|six|seven|eight|nine)'
/**
 * A double play's chain of position numbers: "6-4-3", "six four three", "6
 * to 4 to 3", or "643" run together — never a jersey ("number six").
 */
const POSITION_CHAIN = new RegExp(
  `(?<!(?:number|#)\\s*)(?:\\b(${POSITION_TOKEN}(?:(?:\\s*[-–]\\s*|\\s+to\\s+|\\s+)${POSITION_TOKEN})+)\\b|\\b([1-9]{2,3})\\s*(?=double|d\\.? ?p\\b))`,
  'gi'
)

/**
 * The positions a double play's words name, in order: the first chain of
 * three, or of two right before the double play ("6-3 double play"). A count
 * ("the two-one pitch") is two numbers anywhere else, and is passed over.
 */
function chainSaid(transcript: string): Array<(typeof POSITIONS)[number]> {
  for (const match of transcript.matchAll(POSITION_CHAIN)) {
    const tokens =
      match[1] != null
        ? match[1].split(/\s*[-–]\s*|\s+to\s+|\s+/i)
        : (match[2].match(/[1-9]/g) ?? [])
    const after = transcript.slice(match.index + match[0].length)
    if (tokens.length >= 3 || /^\W*(?:double|d\.? ?p\b)/i.test(after))
      return tokens.map(token => POSITION_WORDS[token.toLowerCase()])
  }
  return []
}

/**
 * What the words say of a double play: done — its words, not denied, or a
 * chain of three position numbers; denied — "no double play", "they
 * couldn't turn two", "they almost turned two", "they try to turn two but
 * the relay is late", or the batter safe at first beside them ("the relay
 * is late", "too late at first"), wherever said; or nothing.
 */
function doublePlaySaid(transcript: string): 'done' | 'denied' | 'unsaid' {
  const matches = [...transcript.matchAll(DOUBLE_PLAY)]
  if (matches.length === 0 && chainSaid(transcript).length < 3) return 'unsaid'
  if (BATTER_SAFE.test(transcript) || RELAY_LATE.test(transcript))
    return 'denied'
  const denied = matches.some(match => {
    const before =
      transcript
        .slice(0, match.index)
        .split(/[.;,]|\bbut\b/i)
        .at(-1) ?? ''
    const after = transcript.slice(match.index + match[0].length)
    return (
      NOT_DONE.test(before) || (TRIED.test(before) && FELL_SHORT.test(after))
    )
  })
  return denied && matches.length > 0 ? 'denied' : 'done'
}

/**
 * The fielders a double play's words name as taking a throw, in order —
 * "throws to the shortstop", "feeds the second baseman" — and the one they
 * say covered first ("the pitcher covers first"), who takes the last throw.
 */
function receiversSaid(transcript: string): {
  all: Array<(typeof POSITIONS)[number]>
  between: Array<(typeof POSITIONS)[number]>
  atFirst?: (typeof POSITIONS)[number]
} {
  const found: Array<{
    index: number
    position: (typeof POSITIONS)[number]
    atFirst: boolean
  }> = []
  for (const [noun, position] of FIELDER_NOUNS)
    for (const match of transcript.matchAll(
      new RegExp(`\\b(?:${noun})\\b`, 'gi')
    )) {
      const before = transcript.slice(0, match.index)
      const after = transcript.slice(match.index + match[0].length)
      const atFirst =
        /^\s+(?:covers|covering|covered|is at|at)\s+first\b/i.test(after)
      if (atFirst || THROWN_TO.test(before) || THROWS.test(after))
        found.push({ index: match.index, position, atFirst })
    }
  found.sort((a, b) => a.index - b.index)
  return {
    all: found.map(item => item.position),
    between: found.filter(item => !item.atFirst).map(item => item.position),
    atFirst: found.find(item => item.atFirst)?.position
  }
}

/** Words before a fielder that say a throw went to him. */
const THROWN_TO =
  /\b(?:(?:throws?|threw|throwing|flips?|flipped|tosses?|tossed|relays?|relayed|relay|fires?|fired|over|on|back)\s+(?:it\s+)?to|feeds?|fed)\s+(?:the\s+)?$/i
/** Words after a fielder that say he threw. */
const THROWS =
  /^\s*,?\s*(?:who\s+)?(?:throws|threw|fires|fired|relays|relayed|flips|flipped)\b/i

/** The base a double play's pivot takes the throw at. */
function pivotBase(
  pivot: (typeof POSITIONS)[number] | undefined
): Destination | undefined {
  return pivot === 'C'
    ? 'home'
    : pivot === '3B'
      ? '3B'
      : pivot === '2B' || pivot === 'SS'
        ? '2B'
        : undefined
}

/** The fielders the words name by position ("the shortstop"). */
function fieldersNamed(transcript: string): Array<(typeof POSITIONS)[number]> {
  return FIELDER_NOUNS.filter(([noun]) =>
    new RegExp(`\\b(?:${noun})\\b`, 'i').test(transcript)
  ).map(([, position]) => position)
}

/**
 * The runners' moves said before the play's result, moved after it: the
 * rules take a fielder's choice only with its runners still on base.
 */
function runnersAfterResult(
  actions: readonly ScoringAction[]
): ScoringAction[] {
  const at = actions.findIndex(action => action.type === 'result')
  if (at < 0) return [...actions]
  const before = actions.slice(0, at)
  return [
    ...before.filter(action => action.type !== 'runnerMovement'),
    actions[at],
    ...before.filter(action => action.type === 'runnerMovement'),
    ...actions.slice(at + 1)
  ]
}

/** Results a denied double play on the ground is sent as. */
const DENIED_RESULTS: ReadonlySet<string> = new Set([
  'field_out',
  'fielders_choice',
  'double_play',
  'grounded_into_double_play'
])

/** Words that put the batter safe at first: the relay to him was late. */
const RELAY_LATE =
  /\b(?:relay|return throw|throw to first|throw to the first baseman) (?:is |was )?(?:too |just |a step )?late\b|\blate (?:on )?the relay\b|\b(?:too |just )?late (?:at|to) first\b|\bnot in time (?:at|to) first\b/i

/**
 * A double play the words deny, on a ball not caught, where the model put a
 * runner out: a fielder's choice, the batter safe. With no runner put out,
 * the model's one out stands — the words may say it was the batter ("no
 * double play, they only get the batter at first") — unless they say the
 * batter was safe ("the relay is late"): then the lead runner was the out,
 * left to the fielder's choice rules.
 */
function noDoublePlay(
  game: GameState,
  actions: readonly ScoringAction[],
  transcript: string
): ScoringAction[] {
  const batter = game.liveData.plays.currentPlay?.matchup.batter.id
  const forced = game.liveData.linescore.offense.first?.id
  const batterSafe = BATTER_SAFE.test(transcript) || RELAY_LATE.test(transcript)
  const runnerOut = actions.some(
    action =>
      action.type === 'runnerMovement' &&
      action.out === true &&
      action.runner !== batter
  )
  const result = actions.find(
    (action): action is ResultAction => action.type === 'result'
  )
  if (
    (!runnerOut && !(batterSafe && forced != null)) ||
    result == null ||
    result.outBy === 'catch' ||
    result.foulCaught === true ||
    !DENIED_RESULTS.has(result.eventType)
  )
    return runnersAfterResult(actions)
  return runnersAfterResult(
    actions.flatMap((action): ScoringAction[] => {
      if (action === result)
        return [{ type: 'result', eventType: 'fielders_choice' }]
      if (action.type !== 'runnerMovement') return [action]
      if (action.runner === batter && action.out === true) return []
      // The lead runner the failed double play got, sent safe to second.
      if (
        !runnerOut &&
        action.runner === forced &&
        action.out !== true &&
        action.to === '2B'
      )
        return []
      return [action]
    })
  )
}

/** Fielders in a row, a fielder who kept the ball named once. */
function chainOf(ids: ReadonlyArray<number | undefined>): number[] {
  const chain: number[] = []
  for (const id of ids) if (id != null && chain.at(-1) !== id) chain.push(id)
  return chain
}

/** The base before each: where the runner forced to it comes from. */
const BEFORE: Partial<Record<Destination, Destination>> = {
  '2B': '1B',
  '3B': '2B',
  home: '3B'
}
const NEXT: Partial<Record<Destination, Destination>> = {
  '1B': '2B',
  '2B': '3B',
  '3B': 'home'
}

/**
 * A ground ball the words call a double play ("six four three double play",
 * "they turn two", "the shortstop fields it, double play") is two outs, with
 * fewer than two out before it: a runner forced out, and the batter out at
 * first.
 *
 * The runner's base comes from the position numbers said (the fielder before
 * the last took the throw there: "one two three" is home), else "home to
 * first" or "third to first", else the out the model sent when it is a base
 * ahead of its runner, else second. The runner is the one forced there, or
 * the one the model put out there.
 *
 * Their fielders are one sequence, read from the first of: the position
 * numbers said; the two outs the model sent when they chain as one double
 * play; a chain of three the model sent; the runner's chain the model sent,
 * then the first baseman; the fielder the ball went to, the fielder covering
 * the base (second base is the second baseman's when the ball went to the
 * shortstop or third baseman, the shortstop's otherwise), then the first
 * baseman. A model's chain counts only when it starts with the fielder the
 * ball went to and its fielder at the runner's base could have taken the
 * throw there. The runner's out takes all but the last of the sequence, the
 * batter's the last two.
 *
 * With nobody out before it, every other runner the batter forced moves up a
 * base, unless the model sent him somewhere.
 */
function doublePlay(
  game: GameState,
  actions: readonly ScoringAction[],
  transcript: string
): ScoringAction[] {
  const outs = game.liveData.linescore.outs
  const said = doublePlaySaid(transcript)
  if (said === 'denied') return noDoublePlay(game, actions, transcript)
  if (outs >= 2 || said === 'unsaid') return [...actions]
  const inPlay = actions.find(
    (action): action is BallInPlayAction => action.type === 'ballInPlay'
  )
  const result = actions.find(
    (action): action is ResultAction => action.type === 'result'
  )
  if (
    inPlay == null ||
    result == null ||
    !DOUBLE_PLAY_RESULTS.has(result.eventType) ||
    result.outBy === 'catch' ||
    result.foulCaught === true ||
    (inPlay.trajectory != null &&
      inPlay.trajectory !== 'ground_ball' &&
      inPlay.trajectory !== 'chopper') ||
    IN_THE_AIR.test(transcript)
  )
    return [...actions]
  const batter = game.liveData.plays.currentPlay?.matchup.batter.id
  if (batter == null) return [...actions]
  // A fielder's choice the model sent, with the batter not put out, is its
  // reading of a double play that did not happen — unless the scorer said
  // its numbers.
  if (
    result.eventType === 'fielders_choice' &&
    chainSaid(transcript).length < 2 &&
    !actions.some(
      action =>
        action.type === 'runnerMovement' &&
        action.runner === batter &&
        action.out === true
    )
  )
    return runnersAfterResult(actions)
  const on = basesOf(game)
  const runnerAt = new Map([...on].map(([id, at]) => [at, id]))
  const moves = actions.filter(
    (action): action is RunnerMovementAction => action.type === 'runnerMovement'
  )
  // A runner's move the model sent counts only ahead of him; his out, only
  // at the next base, where a double play puts him out.
  const ahead = (move: RunnerMovementAction): boolean => {
    const at = on.get(move.runner)
    return at != null && destinationBase(move.to) > destinationBase(at)
  }
  const atNext = (move: RunnerMovementAction): boolean => {
    const at = on.get(move.runner)
    return at != null && destinationBase(move.to) === destinationBase(at) + 1
  }
  const runnerOuts = moves.filter(
    move => move.runner !== batter && move.out === true
  )
  if (runnerOuts.length > 1) return [...actions]
  // Farther than the next base only where the words say so: "he throws home
  // to get the runner from second".
  const named = (to: Destination): boolean =>
    to === 'home'
      ? /\b(?:home|plate|catcher)\b/i.test(transcript)
      : to === '3B'
        ? /\bthird\b/i.test(transcript)
        : false
  const sentOut = runnerOuts.find(
    move => atNext(move) || (ahead(move) && named(move.to))
  )
  const defense = game.liveData.linescore.defense
  const idOf = (position: (typeof POSITIONS)[number]): number | undefined =>
    defense[position]?.id
  const first = idOf('1B')
  const saidPositions = chainSaid(transcript)
  const chainIds = saidPositions.map(idOf)
  // The fielders the words name taking the throws, after the one the ball
  // went to: "he throws to the shortstop covering second, on to first".
  const ballAt = POSITIONS.find(position => idOf(position) === inPlay.fielder)
  const receivers = receiversSaid(transcript)
  // The pivot is the first of them who could take a throw at a base; the
  // first baseman named last takes the throw to first.
  const pivotAt = receivers.between.findIndex(
    position => position !== ballAt && pivotBase(position) != null
  )
  const wordsPivot = pivotAt < 0 ? undefined : receivers.between[pivotAt]
  const between = pivotAt < 0 ? [] : receivers.between.slice(pivotAt)
  const base: Destination =
    pivotBase(saidPositions.at(-2)) ??
    pivotBase(wordsPivot) ??
    (/\bhome to first\b/i.test(transcript)
      ? 'home'
      : /\bthird to first\b/i.test(transcript)
        ? '3B'
        : undefined) ??
    sentOut?.to ??
    '2B'
  const runner =
    sentOut?.to === base ? sentOut.runner : runnerAt.get(BEFORE[base] ?? '1B')
  if (runner == null) return [...actions]
  const sentFor = (id: number): RunnerMovementAction | undefined =>
    moves.find(move => move.runner === id)
  const runnerSent = sentFor(runner)
  const batterSent = sentFor(batter)
  // Who can take the throw at the runner's base: second is the second
  // baseman's or the shortstop's.
  const covers =
    base === '2B'
      ? [idOf('2B'), idOf('SS')]
      : COVERS[base] != null
        ? [idOf(COVERS[base])]
        : []
  const holds = (id: number | undefined): boolean =>
    covers.length === 0 || covers.includes(id)
  // A model's chain counts only when the words account for every fielder
  // in it: the one the ball went to, any named ("the second baseman"), the
  // usual cover at the base, and the first baseman at the end. Otherwise
  // the model made them up, and the usual double play stands.
  const usualCover =
    base === '2B' ? idOf(secondCover(inPlay.fielder, idOf)) : covers[0]
  const accounted = new Set(
    [
      inPlay.fielder,
      usualCover,
      first,
      ...fieldersNamed(transcript).map(idOf)
    ].filter(id => id != null)
  )
  const himself = HIMSELF.test(transcript)
  const startsRight = (chain: readonly number[]): boolean =>
    (inPlay.fielder == null || chain[0] === inPlay.fielder) &&
    chain.every(id => accounted.has(id))
  // The model's runner chain counts when it starts with the fielder who
  // fielded it, its last fielder could take the throw there, and it is more
  // than the fielder who fielded it named again — unless the words say he
  // took the base himself.
  const runnerChainSent =
    runnerSent?.out === true && runnerSent.to === base
      ? chainOf(runnerSent.fielders ?? [])
      : []
  const runnerTrusted =
    runnerChainSent.length > 0 &&
    startsRight(runnerChainSent) &&
    (holds(runnerChainSent.at(-1)) ||
      (himself && runnerChainSent.at(-1) === inPlay.fielder)) &&
    !(
      runnerChainSent.length === 1 &&
      runnerChainSent[0] === inPlay.fielder &&
      !himself
    )
  const batterChainSent =
    batterSent?.out === true ? chainOf(batterSent.fielders ?? []) : []
  const long = [runnerSent?.fielders ?? [], batterSent?.fielders ?? []]
    .map(chainOf)
    .find(
      chain => chain.length >= 3 && startsRight(chain) && holds(chain.at(-2))
    )
  let chains: [number[], number[]] | undefined
  let sequence: number[] = []
  if (chainIds.length >= 2 && chainIds.every(id => id != null))
    sequence = chainOf(chainIds)
  else if (
    inPlay.fielder != null &&
    (wordsPivot != null || receivers.atFirst != null)
  )
    // The fielders the words name: the pivot, and whoever took the throw at
    // first — the one said to cover it, the next named, or the first baseman.
    sequence = chainOf([
      inPlay.fielder,
      wordsPivot != null ? idOf(wordsPivot) : usualCover,
      receivers.atFirst != null
        ? idOf(receivers.atFirst)
        : between[1] != null
          ? idOf(between[1])
          : first
    ])
  else if (
    runnerTrusted &&
    batterChainSent.length > 0 &&
    batterChainSent.length <= 2 &&
    batterChainSent[0] === runnerChainSent.at(-1) &&
    startsRight([inPlay.fielder ?? batterChainSent[0], ...batterChainSent])
  )
    // Two outs the model chained together, as one double play: as sent.
    chains = [runnerChainSent, batterChainSent]
  else if (long != null) sequence = long
  else if (runnerTrusted) sequence = chainOf([...runnerChainSent, first])
  else if (inPlay.fielder != null) {
    const cover =
      base === '2B' ? idOf(secondCover(inPlay.fielder, idOf)) : covers[0]
    sequence = chainOf([inPlay.fielder, cover, first])
  }
  if (chains == null && sequence.length >= 2)
    chains = [chainOf(sequence.slice(0, -1)), chainOf(sequence.slice(-2))]
  const runnerOut: RunnerMovementAction = {
    type: 'runnerMovement',
    runner,
    to: base,
    out: true,
    ...(chains != null
      ? { fielders: chains[0] }
      : runnerSent?.out === true && runnerSent.fielders != null
        ? { fielders: runnerSent.fielders }
        : {})
  }
  const batterOut: RunnerMovementAction = {
    type: 'runnerMovement',
    runner: batter,
    to: '1B',
    out: true,
    ...(chains != null
      ? { fielders: chains[1] }
      : batterSent?.out === true && batterSent.fielders != null
        ? { fielders: batterSent.fielders }
        : {})
  }
  // Every other runner the batter forced moves up a base, when the inning
  // goes on and the model sent him nowhere a base ahead.
  const forcedUp: RunnerMovementAction[] = []
  // Lead runner first: one who holds keeps the runners behind him where
  // they are too.
  const heldAt = new Set<Destination>()
  if (outs === 0)
    for (const at of ['3B', '2B', '1B'] as const) {
      const id = runnerAt.get(at)
      const forced = (['1B', '2B', '3B'] as const)
        .slice(0, destinationBase(at))
        .every(behind => runnerAt.has(behind))
      if (id == null || id === runner || !forced) continue
      // Sent ahead, or held: "the runner on second holds".
      const sent = sentFor(id)
      if (sent != null && sent.out !== true && ahead(sent)) continue
      // A hold another runner's move contradicts is not one: he was forced on.
      const taken = moves.some(
        move =>
          move.runner !== id &&
          move.out !== true &&
          move.to === at &&
          ahead(move)
      )
      if (
        (sent != null && sent.out !== true && sent.to === at && !taken) ||
        heldAt.has(NEXT[at]!)
      ) {
        heldAt.add(at)
        continue
      }
      forcedUp.push({ type: 'runnerMovement', runner: id, to: NEXT[at]! })
    }
  const replaced = new Set([
    runner,
    batter,
    ...forcedUp.map(move => move.runner),
    // The out the model sent, replaced by the double play's own.
    ...runnerOuts.map(move => move.runner)
  ])
  // The runners' moves after the result, the double play's among them,
  // lead runner first so no one is moved onto an occupied base; the batter's
  // out last.
  const at = actions.indexOf(result)
  const others = actions
    .slice(at + 1)
    .filter(
      (action): action is RunnerMovementAction =>
        action.type === 'runnerMovement' && !replaced.has(action.runner)
    )
  const runners = [runnerOut, ...forcedUp, ...others].sort(
    (a, b) =>
      destinationBase(on.get(b.runner) ?? '1B') -
      destinationBase(on.get(a.runner) ?? '1B')
  )
  return [
    ...actions
      .slice(0, at + 1)
      .filter(
        action =>
          action.type !== 'runnerMovement' || !replaced.has(action.runner)
      ),
    ...runners,
    ...actions.slice(at + 1).filter(action => action.type !== 'runnerMovement'),
    batterOut
  ]
}

/** A fielder who made the out at the base himself. */
const HIMSELF =
  /\b(?:steps? on|stepped on|touch(?:es|ed)?|tags?|tagged|unassisted|himself|on his own)\b/i

/** Results a dictated ground-ball double play may be sent as. */
const DOUBLE_PLAY_RESULTS: ReadonlySet<string> = new Set([
  'field_out',
  'fielders_choice',
  'double_play',
  'grounded_into_double_play'
])

/** The base a runner stealing this one comes from. */
const FROM: Record<Destination, Destination> = {
  batter: 'batter',
  '1B': 'batter',
  '2B': '1B',
  '3B': '2B',
  home: '3B'
}
const SPOKEN_BASE = /\b(first|second|third|home|plate|1st|2nd|3rd|1b|2b|3b)\b/i
/** "The first pitch" names no base. */
const PITCH_ORDINAL = /\b(first|second|third|next|1st|2nd|3rd)\s+pitch\b/gi
/** "The lead runner", "the trail runner". */
const RUNNER_BY_PLACE = /\b(lead|leading|trail|trailing)\s+runner\b/i
const NUMBER_WORDS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'thirteen',
  'fourteen',
  'fifteen',
  'sixteen',
  'seventeen',
  'eighteen',
  'nineteen',
  'twenty'
]

/**
 * Whether the words name a base ("second"; "the first pitch" is no base) or
 * a runner on base — by first name, "#2", "number 2", "number two", or as
 * the lead or trail runner.
 */
function wordsNameRunnerOrBase(game: GameState, transcript: string): boolean {
  if (SPOKEN_BASE.test(transcript.replace(PITCH_ORDINAL, ' '))) return true
  if (RUNNER_BY_PLACE.test(transcript)) return true
  const offense = game.liveData.linescore.offense
  const runners = [offense.first?.id, offense.second?.id, offense.third?.id]
  return runners.some(id => id != null && namesPlayer(game, id, transcript))
}

/** Whether the words name a player: by first name, "#2" or "number two". */
function namesPlayer(game: GameState, id: number, words: string): boolean {
  return playerPatterns(game, id).some(pattern =>
    new RegExp(pattern, 'i').test(words)
  )
}

/** The ways the words may name a player, as regular expressions. */
export function playerPatterns(game: GameState, id: number): string[] {
  const player = game.gameData.players[id]
  const model = game.meta.modelNames?.[id]?.split(' ')[0]
  const first =
    player?.firstName ??
    (model == null || model === '' ? undefined : model) ??
    player?.fullName.split(' ')[0]
  const jersey = player?.jersey.trim() ?? ''
  const escape = (text: string): string =>
    text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return [
    ...(first == null || first === '' ? [] : [`\\b${escape(first)}\\b`]),
    ...(/^\d+$/.test(jersey)
      ? [
          `(#|\\bnumber\\s+)${jersey}\\b`,
          ...(NUMBER_WORDS[Number(jersey)] == null
            ? []
            : [`\\bnumber\\s+${NUMBER_WORDS[Number(jersey)]}\\b`])
        ]
      : [])
  ]
}

/**
 * A question a sentence raises before it is tried, as a whole sentence per
 * answer:
 * - a ball caught with nobody to credit: who caught it;
 * - a ball in play with nothing said of how it ended: what happened;
 * - one steal whose words name no base and no runner, with more than one
 *   runner who could have stolen: who stole (not asked again of an answer).
 */
export function sentenceQuestion(
  game: GameState,
  transcript: string,
  sentence: readonly ScoringAction[],
  answered = false
): Question | undefined {
  const defense = game.liveData.linescore.defense
  // Each caught out, with the ball in play since the result before it.
  let previous = -1
  for (let at = 0; at < sentence.length; at++) {
    const result = sentence[at]
    if (result.type !== 'result') continue
    const inPlayAt = sentence.findIndex(
      (action, index) =>
        index > previous && index < at && action.type === 'ballInPlay'
    )
    previous = at
    const inPlay = sentence[inPlayAt]
    if (
      result.eventType !== 'field_out' ||
      result.outBy !== 'catch' ||
      (inPlay?.type === 'ballInPlay' && inPlay.fielder != null)
    )
      continue
    // With two batters in the sentence, say whose ball it was.
    const results = sentence.filter(action => action.type === 'result').length
    let upTo = game
    for (const action of sentence.slice(0, at))
      upTo = applyAction(upTo, action).state
    const whose = upTo.liveData.plays.currentPlay?.matchup.batter.id
    return {
      question:
        results > 1 && whose != null
          ? `Who caught ${nameOf(game, whose)}'s ball?`
          : 'Who caught it?',
      options: POSITIONS.flatMap(position => {
        const ref = defense[position]
        if (ref == null) return []
        const withFielder =
          inPlay?.type === 'ballInPlay'
            ? sentence.map(action =>
                action === inPlay ? { ...inPlay, fielder: ref.id } : action
              )
            : [
                ...sentence.slice(0, at),
                {
                  type: 'ballInPlay' as const,
                  contact: 'swing' as const,
                  fielder: ref.id
                },
                ...sentence.slice(at)
              ]
        return [
          { label: `${position} ${nameOf(game, ref.id)}`, actions: withFielder }
        ]
      })
    }
  }
  const resultAt = sentence.findIndex(action => action.type === 'result')
  const inPlayAt = sentence.findIndex(action => action.type === 'ballInPlay')
  const inPlay = sentence[inPlayAt]
  if (inPlay?.type === 'ballInPlay' && resultAt < 0) {
    const ended = (
      label: string,
      how: ResultAction
    ): { label: string; actions: ScoringAction[] } => ({
      label,
      actions: [
        ...sentence.slice(0, inPlayAt + 1),
        how,
        ...sentence.slice(inPlayAt + 1)
      ]
    })
    return {
      question: 'What happened on the play?',
      options: [
        ended('Caught for an out', {
          type: 'result',
          eventType: 'field_out',
          outBy: 'catch'
        }),
        ended('Thrown out', {
          type: 'result',
          eventType: 'field_out',
          outBy: 'throw'
        }),
        ended("Fielder's choice", {
          type: 'result',
          eventType: 'fielders_choice'
        }),
        ended('Single', { type: 'result', eventType: 'single' }),
        ended('Double', { type: 'result', eventType: 'double' }),
        ended('Triple', { type: 'result', eventType: 'triple' }),
        ended('Home run', { type: 'result', eventType: 'home_run' }),
        ended('Reached on an error', {
          type: 'result',
          eventType: 'field_error'
        })
      ]
    }
  }
  // A steal, or a runner caught stealing, said with no base and no runner.
  const steals = sentence.filter(
    action =>
      action.type === 'baseRunning' &&
      (action.kind === 'steal' ||
        action.kind === 'caught_stealing' ||
        action.kind === 'pickoff')
  )
  const said = steals[0] as Extract<ScoringAction, { type: 'baseRunning' }>
  const caughtStealing = said?.kind === 'caught_stealing'
  const pickoff = said?.kind === 'pickoff'
  if (
    !answered &&
    steals.length === 1 &&
    sentence.every(action => action === steals[0] || action.type === 'pitch') &&
    !wordsNameRunnerOrBase(game, transcript)
  ) {
    const offense = game.liveData.linescore.offense
    const runners = [offense.third?.id, offense.second?.id, offense.first?.id]
    const on = runners.flatMap((id, index) =>
      id == null ? [] : [{ id, to: (['home', '3B', '2B'] as const)[index] }]
    )
    if (on.length < 2) return undefined
    const pickedAt = pickoff ? pickoffBase(game, said) : undefined
    // The model's action for another runner; its fielders only where the
    // base is the one it sent.
    const steal = (runner: { id: number; to: Destination }): ScoringAction => {
      const { fielders: _guessed, ...rest } = said
      // A pickoff's base is the one the runner stood on; a steal's the one
      // he ran for.
      const sameBase = pickoff
        ? pickedAt == null
          ? runner.id === said.runner
          : pickedAt === FROM[runner.to]
        : runner.to === said.to
      return {
        ...(sameBase ? said : rest),
        runner: runner.id,
        to: pickoff ? FROM[runner.to] : runner.to
      }
    }
    // Alone, a runner steals only into an empty base.
    const empty = (to: Destination): boolean =>
      to === 'home' ||
      (to === '3B' ? offense.third == null : offense.second == null)
    // A pickoff can be of any runner; a steal only into an empty base.
    const free = pickoff ? on : on.filter(runner => empty(runner.to))
    // Caught stealing with one runner who could have tried: it was him.
    if (caughtStealing && free.length < 2) return undefined
    // Each answer is the sentence, its steal replaced.
    const withSteals = (chosen: ScoringAction[]): ScoringAction[] =>
      sentence.flatMap(action => (action === steals[0] ? chosen : [action]))
    return {
      question: pickoff
        ? 'Who was picked off?'
        : caughtStealing
          ? 'Who was caught stealing?'
          : 'Who stole?',
      options: [
        ...free.map(runner => ({
          label: pickoff
            ? `${nameOf(game, runner.id)} picked off ${SPOKEN[FROM[runner.to]]}`
            : `${nameOf(game, runner.id)} ${caughtStealing ? 'caught stealing' : 'steals'} ${SPOKEN[runner.to]}`,
          actions: withSteals([steal(runner)])
        })),
        ...(caughtStealing || pickoff
          ? []
          : [
              {
                label: on.length === 2 ? 'Double steal' : 'Triple steal',
                actions: withSteals(on.map(steal))
              }
            ])
      ]
    }
  }
  return undefined
}

/** The base a fielder takes a pickoff throw at. */
const PICKOFF_COVERS: Partial<Record<(typeof POSITIONS)[number], Destination>> =
  { '1B': '1B', '2B': '2B', SS: '2B', '3B': '3B' }

/**
 * Where a pickoff was: at the base of the fielder who took the throw, or
 * else the base it was sent with.
 */
function pickoffBase(
  game: GameState,
  pickoff: Extract<ScoringAction, { type: 'baseRunning' }>
): Destination | undefined {
  const defense = game.liveData.linescore.defense
  const receiver = pickoff.fielders?.at(-1)
  const position = POSITIONS.find(
    place => receiver != null && defense[place]?.id === receiver
  )
  return (position == null ? undefined : PICKOFF_COVERS[position]) ?? pickoff.to
}
/** Each base as the words say it; "home" also as "the plate". */
const SAID_BASE: Partial<Record<number, RegExp>> = {
  2: /\b(?:second|2nd|2b)\b(?! base(?:man|men)\b)/i,
  3: /\b(?:third|3rd|3b)\b(?! base(?:man|men)\b)/i,
  4: /\b(?:home|plate)\b/i
}
const STEAL_WORD = /\b(?:steals?|stole|stolen|stealing|swipes?|swiped)\b/i
/** A runner by his base: "the runner on first", "the man at 2nd". */
const RUNNER_ON =
  /\b(?:base ?runner|runner|man|guy|kid|girl|boy|player)(?: over| standing)? (?:on|at|from) (first|second|third|1st|2nd|3rd)\b/gi
const BASE_OF: Record<string, number> = {
  first: 1,
  '1st': 1,
  second: 2,
  '2nd': 2,
  third: 3,
  '3rd': 3
}
/** The base a throw went to: "the throw to third is late". */
const THROW_TO =
  /\bthrow\w*\b(?:(?!\band\b)[^.;,])*?\b(?:to|into|at|toward|towards) (?:the )?(?:first|second|third|home|plate|1st|2nd|3rd)\b/gi
/** The base word at the end of a throw's words. */
const THROWN_BASE =
  /\b(?:to|into|at|toward|towards) (?:the )?(?:first|second|third|home|plate|1st|2nd|3rd)$/i
/** "Scores", "races home", "crosses the plate": a runner reached home. */
const SAID_SCORED =
  /\b(?:scores?|scored|scoring|(?:comes?|came|goes|went|races?|raced|heads?|headed|runs?|ran|dash(?:es|ed)?|slides?|slid|makes it|made it) home|cross(?:es|ed)? the plate)\b/i
/** A run said not to have scored: "does not score", "thrown out trying to score". */
const NOT_SCORED =
  /(?:\bnot\b|\bnever\b|n't\b)(?:\s+\w+){0,2}\s+(?:score|scores|scored|come home|go home|make it home|cross)|\b(?:trying|tries|tried|attempting|attempts) to (?:score|go home|come home|make it home|steal home)\b|\b(?:thrown|tagged|cut down|gunned down)(?: out)?(?:\s+\w+){0,2}\s+(?:at home|at the plate|trying to score)\b|\bout at (?:home|the plate)\b/i

/**
 * The words that say something of this runner, clause by clause (split at
 * ".", ";", "and" and "but"), each one the test finds. A clause is cut at
 * every runner it names — by his base ("the runner on third", "the kid at
 * 3rd") or by name or number, among the runners on base as the sentence
 * began or now — and each piece is about the runner it begins with; the
 * words before the first runner named are this runner's ("he steals"). The
 * pieces about other runners are dropped, and so are the base a throw went
 * to and this runner's own "runner on first".
 */
function runnerClauses(
  games: readonly GameState[],
  transcript: string,
  runner: number,
  from: number,
  test: RegExp
): string[] {
  const others = [
    ...new Set(
      games.flatMap(game => {
        const offense = game.liveData.linescore.offense
        return [offense.first?.id, offense.second?.id, offense.third?.id]
      })
    )
  ].filter((id): id is number => id != null && id !== runner)
  const said = (
    clause: string,
    ids: readonly number[]
  ): Array<{ at: number; end: number }> =>
    ids
      .flatMap(id => games.flatMap(game => playerPatterns(game, id)))
      .flatMap(pattern => [...clause.matchAll(new RegExp(pattern, 'gi'))])
      .map(match => ({ at: match.index, end: match.index + match[0].length }))
  const own = (clause: string): string => {
    const named = [
      ...[...clause.matchAll(RUNNER_ON)].map(match => ({
        at: match.index,
        end: match.index + match[0].length,
        mine: BASE_OF[match[1].toLowerCase()] === from
      })),
      ...said(clause, [runner]).map(at => ({ ...at, mine: true })),
      ...said(clause, others).map(at => ({ ...at, mine: false }))
    ].sort((a, b) => a.at - b.at)
    let kept = clause.slice(0, named[0]?.at ?? clause.length)
    named.forEach((at, index) => {
      if (at.mine)
        kept += ` ${clause.slice(at.end, named[index + 1]?.at ?? clause.length)}`
    })
    return kept
  }
  return transcript
    .replace(PITCH_ORDINAL, ' ')
    .replace(THROW_TO, thrown => thrown.replace(THROWN_BASE, ' '))
    .split(/[.;]|\b(?:and|but)\b/i)
    .map(own)
    .filter(clause => test.test(clause))
}

/**
 * The fielder the words say threw: "the catcher picks him off", "pitcher
 * to first". The first one said, so "pitcher to the second baseman" is the
 * pitcher's throw.
 */
function throwerSaid(
  transcript: string
): (typeof POSITIONS)[number] | undefined {
  let found: { index: number; position: (typeof POSITIONS)[number] } | undefined
  for (const [noun, position] of FIELDER_NOUNS) {
    const re = new RegExp(
      `\\b(?:by|from)\\s+(?:the\\s+)?(?:${noun})\\b|\\b(?:${noun})\\b(?:'s)?\\s+throw|\\b(?:${noun})\\b[^.]{0,24}\\b(?:throws|threw|throwing|picks|picked)\\b|\\b(?:${noun})\\b(?=\\s+to\\b)`,
      'gi'
    )
    const match = re.exec(transcript)
    if (match != null && (found == null || match.index < found.index))
      found = { index: match.index, position }
  }
  return found?.position
}

/**
 * Fielders for a caught stealing or a pickoff the play left blank. A caught
 * stealing is the catcher's throw: the pitcher gets no assist after the
 * pitch. A pickoff is the pitcher's throw unless the words name who threw.
 * The fielder at the base gets the putout. At second that is the same cover
 * as any other throw there.
 */
function runningOutFielders(
  game: GameState,
  action: Extract<ScoringAction, { type: 'baseRunning' }>,
  transcript: string
): number[] | undefined {
  const defense = game.liveData.linescore.defense
  const idOf = (position: (typeof POSITIONS)[number]): number | undefined =>
    defense[position]?.id
  const said = throwerSaid(transcript)
  const to = action.to
  if (to == null) return undefined
  if (action.kind === 'caught_stealing') {
    const throwerPos = said != null && said !== 'P' ? said : 'C'
    const thrower = idOf(throwerPos)
    if (thrower == null) return undefined
    const cover = coverAt(to, thrower, idOf)
    const receiver = cover == null ? undefined : idOf(cover)
    if (receiver == null || receiver === thrower) return [thrower]
    return [thrower, receiver]
  }
  if (action.kind !== 'pickoff') return undefined
  const throwerPos = said ?? 'P'
  const thrower = idOf(throwerPos)
  if (thrower == null) return undefined
  const cover =
    to === '2B' || to === '1B' || to === '3B' || to === 'home'
      ? coverAt(to, thrower, idOf)
      : undefined
  const receiver = cover == null ? undefined : idOf(cover)
  if (receiver == null || receiver === thrower) return [thrower]
  return [thrower, receiver]
}

const BASE_INDEX: Partial<Record<Destination, number>> = {
  '2B': 2,
  '3B': 3,
  home: 4
}

/**
 * A steal, or a runner caught stealing, names the base taken, and with it
 * the runner on the base before it. One sent for a runner who could not
 * have tried it goes to the runner who could, when nothing else in the
 * sentence moves him: the runner on the base before the one named, or — the
 * base ahead of the runner sent being taken, and the words naming no base
 * and no runner — the runner on it, as far up as the next empty base.
 * A steal takes one base, unless its own words — the clauses that say it,
 * not about another runner, without the base a throw went to — say the base
 * taken and not the one before it, and never more than two. A steal of home
 * kept to one base, whose runner his own words say scored (and not that he
 * did not), is followed by his run.
 * A pickoff is of the runner on the base it was at. Fielders sent for
 * another base are not kept. A caught stealing left with no fielders is
 * the catcher's throw to the fielder at the base, and a pickoff that gets
 * the runner is the pitcher's throw there, unless someone else threw. A
 * pickoff the runner is safe on credits nobody.
 */
export function stealsByBase(
  game: GameState,
  said: readonly ScoringAction[],
  transcript = ''
): ScoringAction[] {
  // A runner or base the words name is the scorer's: nothing is moved up.
  const unnamed = !wordsNameRunnerOrBase(game, transcript)
  const moving = new Set(
    said.flatMap(action =>
      (action.type === 'baseRunning' || action.type === 'runnerMovement') &&
      action.runner != null
        ? [action.runner]
        : []
    )
  )
  const TO: readonly Destination[] = ['batter', '1B', '2B', '3B', 'home']
  const fixed: ScoringAction[] = []
  let state = game
  for (const action of said) {
    let next = action
    if (action.type === 'baseRunning' && action.kind === 'pickoff') {
      // A pickoff at a base is of the runner standing on it.
      const offense = state.liveData.linescore.offense
      const standing = {
        '1B': offense.first,
        '2B': offense.second,
        '3B': offense.third
      }
      const base = pickoffBase(state, action)
      const at =
        base == null ? undefined : standing[base as '1B' | '2B' | '3B']?.id
      if (at != null && at !== action.runner)
        next = { ...action, runner: at, to: base }
    }
    if (
      action.type === 'baseRunning' &&
      (action.kind === 'steal' || action.kind === 'caught_stealing') &&
      action.to != null &&
      BASE_INDEX[action.to] != null
    ) {
      const offense = state.liveData.linescore.offense
      const bases = [offense.first?.id, offense.second?.id, offense.third?.id]
      const from = bases.indexOf(action.runner) + 1
      const named = BASE_INDEX[action.to]!
      const holder = bases[named - 2]
      let runner = action.runner
      // A runner steals one base at a time, unless the words say the base
      // he took and not the one before it.
      const words = runnerClauses(
        [game, state],
        transcript,
        action.runner,
        from,
        STEAL_WORD
      ).join(' ')
      // Never more than two bases in one steal.
      const farther =
        named - from <= 2 &&
        SAID_BASE[named]?.test(words) === true &&
        SAID_BASE[from + 1]?.test(words) !== true
      let to = from > 0 && !farther ? Math.min(named, from + 1) : named
      const target = to
      if (
        holder != null &&
        holder !== action.runner &&
        from !== named - 1 &&
        !moving.has(holder)
      ) {
        // The base named is the one taken: its runner came from the base
        // before it.
        runner = holder
        to = named
      } else if (unnamed && from > 0 && from < 4) {
        // The base ahead is taken: the runner on it went, or the one ahead.
        to = from + 1
        while (to < 4 && bases[to - 1] != null && !moving.has(bases[to - 1]!)) {
          runner = bases[to - 1]!
          to++
        }
        if (runner === action.runner) to = target
      }
      if (runner !== action.runner || to !== named) {
        const { fielders: _guessed, ...rest } = action
        next = {
          ...(to === named ? action : rest),
          runner,
          to: TO[to]
        }
      }
    }
    if (
      next.type === 'baseRunning' &&
      (next.kind === 'caught_stealing' || next.kind === 'pickoff') &&
      (next.fielders == null || next.fielders.length === 0)
    ) {
      const fielders = runningOutFielders(state, next, transcript)
      if (fielders != null) next = { ...next, fielders }
    }
    const prior = state
    fixed.push(next)
    state = applyAction(state, next).state
    // A steal of home kept to third, whose runner his own words say scored:
    // the run came after.
    const scored = (runner: number): boolean => {
      const offense = prior.liveData.linescore.offense
      const from =
        [offense.first?.id, offense.second?.id, offense.third?.id].indexOf(
          runner
        ) + 1
      return runnerClauses(
        [game, prior],
        transcript,
        runner,
        from,
        SAID_SCORED
      ).some(clause => !NOT_SCORED.test(clause))
    }
    if (
      action.type === 'baseRunning' &&
      action.kind === 'steal' &&
      action.to === 'home' &&
      next.type === 'baseRunning' &&
      next.to !== 'home' &&
      scored(next.runner) &&
      !said.some(
        other =>
          (other.type === 'runnerMovement' &&
            other.runner === next.runner &&
            other.to === 'home') ||
          (other.type === 'batteryEvent' &&
            other.advances?.some(
              move => move.runner === next.runner && move.to === 'home'
            ) === true)
      )
    ) {
      const run: ScoringAction = {
        type: 'runnerMovement',
        runner: next.runner,
        to: 'home'
      }
      fixed.push(run)
      state = applyAction(state, run).state
    }
  }
  return fixed
}

/**
 * Runners who move together — a double steal, runners said one by one moving
 * up between pitches — in the order the game takes them: the lead runner
 * first, whatever order they were said in. Everything else keeps its place.
 */
export function leadRunnersFirst(
  game: GameState,
  said: readonly ScoringAction[]
): ScoringAction[] {
  const together = (state: GameState, action: ScoringAction): boolean =>
    action.type === 'baseRunning' ||
    (action.type === 'runnerMovement' && state.pending == null)
  const base = (state: GameState, runner: number): number => {
    const offense = state.liveData.linescore.offense
    return [offense.first, offense.second, offense.third].findIndex(
      ref => ref?.id === runner
    )
  }
  const ordered: ScoringAction[] = []
  let state = game
  for (let index = 0; index < said.length;) {
    let end = index
    while (end < said.length && together(state, said[end])) end++
    const run =
      end === index
        ? [said[index]]
        : said.slice(index, end).sort((a, b) => {
            const lead = (action: ScoringAction): number =>
              'runner' in action && action.runner != null
                ? base(state, action.runner)
                : -1
            return lead(b) - lead(a)
          })
    for (const action of run) {
      ordered.push(action)
      state = applyAction(state, action).state
    }
    index = Math.max(end, index + 1)
  }
  return ordered
}

/** Only words unambiguously belonging to the play being completed. */
function completionWords(
  initial: GameState,
  current: GameState,
  sentence: readonly ScoringAction[],
  transcript: string
): string {
  const index =
    current.liveData.plays.allPlays.length -
    initial.liveData.plays.allPlays.length
  const parts = transcript.split(/\b(?:next|following)\s+(?:batter|hitter)\b/i)
  if (parts.length > 1) return parts[index] ?? ''
  // Without a clear spoken boundary, keep canonical fielders and normal
  // base coverage rather than borrowing a receiver from another batter.
  return index > 0 ||
    sentence.filter(action => action.type === 'result').length > 1
    ? ''
    : transcript
}

/**
 * A play that still waits on something a tap would settle — the batter out
 * at first on a ground out nobody finished, the runners out on a fielder's
 * choice — when the sentence goes on with something the game will not take
 * until it is settled: a runner's move, or the next batter. It is settled
 * by the same rules as the end of a sentence, so the game takes the rest;
 * the batter's own out said later in the sentence is taken first instead.
 * What only the scorer can answer is asked, each answer the whole sentence.
 * A runner said to reach the base the play already put him on — "single,
 * the runner goes to third" from second — is where the game has him.
 */
export function settleBetween(
  game: GameState,
  sentence: readonly ScoringAction[],
  transcript = ''
): { actions: ScoringAction[]; question?: Question } {
  const settled: ScoringAction[] = []
  const rest = [...sentence]
  const deferred = new Set<ScoringAction>()
  let state = game
  while (rest.length > 0) {
    const action = rest.shift()!
    if (
      state.pending?.outcome === "Fielder's Choice" &&
      state.liveData.linescore.outs === 2 &&
      action.type === 'runnerMovement' &&
      action.out === true
    ) {
      const batter = state.liveData.plays.currentPlay!.matchup.batter.id
      const at = rest.findIndex(
        move =>
          move.type === 'runnerMovement' &&
          move.runner === batter &&
          move.out !== true &&
          move.to === '1B'
      )
      if (at >= 0) {
        const batterSafe = rest[at]
        const applied = applyAction(state, batterSafe)
        if (applied.problems.length === 0) {
          state = applied.state
          settled.push(batterSafe)
          rest.splice(at, 1)
        }
      }
    }
    const earlierHomes = settled.filter(
      (move): move is RunnerMovementAction =>
        move.type === 'runnerMovement' &&
        move.out !== true &&
        move.to === 'home'
    )
    const homeNow =
      action.type === 'runnerMovement' &&
      action.out !== true &&
      action.to === 'home'
    if (
      state.pending?.outcome === "Fielder's Choice" &&
      (homeNow || earlierHomes.length > 0)
    ) {
      const batter = state.liveData.plays.currentPlay!.matchup.batter.id
      if (
        state.pending.movements.some(
          move => move.playerId === batter && move.decision === 'pending'
        ) &&
        ![action, ...rest].some(
          move => move.type === 'runnerMovement' && move.runner === batter
        )
      ) {
        const batterSafe = safe(batter, '1B')
        const applied = applyAction(state, batterSafe)
        if (applied.problems.length === 0) {
          state = applied.state
          settled.push(batterSafe)
        }
      }
      for (const waiting of state.pending?.movements ?? []) {
        const call = [action, ...rest].find(
          (move): move is RunnerMovementAction =>
            move.type === 'runnerMovement' &&
            move.out === true &&
            move.runner === waiting.playerId
        )
        if (
          waiting.decision !== 'pending' ||
          waiting.from === 0 ||
          call == null ||
          (destinationBase(call.to) === waiting.from + 1 &&
            forcedFrom(state, waiting.from))
        )
          continue
        let to = waiting.from
        while (
          to <= destinationBase(call.to) &&
          runnerDecisionError(state, {
            playerId: waiting.playerId,
            from: waiting.from,
            to
          }) != null
        )
          to++
        if (to > destinationBase(call.to)) continue
        const hold = safe(waiting.playerId, BASE_NAMES[to])
        const applied = applyAction(state, hold)
        if (applied.problems.length === 0) {
          state = applied.state
          settled.push(hold)
        }
      }
      for (const home of earlierHomes)
        if (
          state.pending?.choiceApplied === true &&
          state.homeQueue.includes(home.runner)
        ) {
          // Release the earlier crossing once its blockers are settled.
          // Its defensive relay was already submitted with the first call.
          const {
            fielders: _fielders,
            locations: _locations,
            runnerLocation: _runnerLocation,
            ...crossing
          } = home
          const applied = applyAction(state, crossing)
          if (applied.problems.length === 0) {
            state = applied.state
            settled.push(crossing)
          }
        }
    }
    if (
      action.type === 'runnerMovement' &&
      action.out !== true &&
      action.fieldingRoute == null &&
      state.pending?.movements.some(
        move =>
          move.playerId === action.runner &&
          move.decision === 'safe' &&
          move.to === destinationBase(action.to)
      ) === true
    )
      continue
    const waits =
      settled.length > 0 &&
      state.pending != null &&
      applyAction(state, action).problems.length > 0
    // A non-forced lead runner can still occupy a Safe trailer's destination.
    // Delay that non-scoring advance until the spoken lead move frees it;
    // keep all home crossings and Out calls in their stated order.
    if (
      waits &&
      state.pending?.outcome === "Fielder's Choice" &&
      action.type === 'runnerMovement' &&
      action.out !== true &&
      destinationBase(action.to) < 4 &&
      !deferred.has(action) &&
      rest.some(next => next.type === 'runnerMovement')
    ) {
      deferred.add(action)
      rest.push(action)
      continue
    }
    if (waits) {
      const decision = nextDecision(state)
      const batter = batterOf(state)
      const ownOut = rest.findIndex(
        later =>
          later.type === 'runnerMovement' &&
          later.runner === batter &&
          later.out === true
      )
      const answer =
        decision === 'batterOut' && ownOut >= 0
          ? rest.splice(ownOut, 1)
          : decide(
              state,
              decision,
              settled,
              completionWords(game, state, sentence, transcript)
            )
      if (answer != null && !Array.isArray(answer)) {
        const { ask } = answer
        const after = [action, ...rest]
        return {
          actions: [...sentence],
          question: {
            question: ask.question,
            ...(ask.options == null
              ? {}
              : {
                  options: ask.options.map(option => ({
                    label: option.label,
                    actions: [...settled, ...option.actions, ...after]
                  }))
                }),
            ...(ask.pick == null
              ? {}
              : {
                  pick: {
                    ...ask.pick,
                    before: [...settled, ...ask.pick.before],
                    after
                  }
                })
          }
        }
      }
      for (const step of answer ?? []) {
        const applied = applyAction(state, step)
        if (applied.problems.length > 0) break
        state = applied.state
        settled.push(step)
      }
    }
    state = applyAction(state, action).state
    settled.push(action)
  }
  return { actions: settled }
}

export function completeSentence(
  game: GameState,
  sentence: readonly ScoringAction[],
  transcript = ''
): { actions: ScoringAction[] } | { question: Question } {
  let state = game
  for (const action of sentence) state = applyAction(state, action).state
  const actions = [...sentence]
  for (let step = 0; step < 12; step++) {
    const answer = decide(
      state,
      nextDecision(state),
      sentence,
      completionWords(game, state, sentence, transcript)
    )
    if (answer == null) return { actions }
    if (!Array.isArray(answer)) {
      const { ask } = answer
      return {
        question: {
          question: ask.question,
          ...(ask.options == null
            ? {}
            : {
                options: ask.options.map(option => ({
                  label: option.label,
                  actions: [...actions, ...option.actions]
                }))
              }),
          ...(ask.pick == null
            ? {}
            : {
                pick: { ...ask.pick, before: [...actions, ...ask.pick.before] }
              })
        }
      }
    }
    for (const action of answer) {
      const applied = applyAction(state, action)
      // The rules would not take it: leave the rest to the screen.
      if (applied.problems.length > 0) return { actions }
      state = applied.state
      actions.push(action)
    }
  }
  return { actions }
}

/** The whole sentence a question's answer comes to. */
export function answerOf(
  question: DictationQuestion,
  answer: { option?: number; picks?: readonly number[] }
): ScoringAction[] | undefined {
  if (question.options != null)
    return answer.option == null
      ? undefined
      : question.options[answer.option]?.actions
  const pick = question.pick
  if (pick == null || answer.picks == null) return undefined
  const picks = [...new Set(answer.picks)]
    .filter(index => index >= 0 && index < pick.choices.length)
    .sort((a, b) => a - b)
  // The picked outs join the sentence, and the rules finish it from there.
  return picks.length === 0
    ? [...pick.before, ...pick.none, ...(pick.after ?? [])]
    : [
        ...pick.before,
        ...picks.flatMap(index => pick.choices[index].actions),
        ...(pick.after ?? [])
      ]
}
